import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, RotateCcw, ShieldAlert, Inbox, Users, Trash2, Crown, Skull, Lock, Unlock, Pencil, Heart, Flag, Plus, Eye, EyeOff, Copy, Music, Upload, BookOpen, Save, Compass, Megaphone, AlertOctagon, RefreshCw, Package, Camera } from "lucide-react";
import { api } from "@/lib/api";
import { useDiscord } from "@/context/DiscordContext";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import FactionChip from "@/components/site/FactionChip";
import { FACTION_COLORS, factionTextColor } from "@/lib/factionColors";
import { RulesSection } from "./admin/RulesEditor";
import { GuideSection } from "./admin/GuideEditor";
import { MediaSection } from "./admin/MediaEditor";
import { TextField, IconBtn, MarkdownTextArea } from "./admin/adminPrimitives";
import { WelcomeBar, DEFAULT_COLORS } from "../components/site/AnnouncementBar";
import useDragFocus from "@/lib/useDragFocus";

// Each admin tab declares which permission a non-super-admin needs to see it.
// `superAdminOnly: true` hides the tab from everyone except super-admins even
// if the underlying write endpoint is gated separately. `perm: null` means
// the tab is visible to any staff row (currently unused).
const ADMIN_TABS = [
  { id: "applications", label: "Applications", icon: Inbox, perm: "applications" },
  { id: "site", label: "Site", icon: Megaphone, superAdminOnly: true },
  { id: "members", label: "Members", icon: Skull, perm: "members" },
  { id: "factions", label: "Factions", icon: Flag, perm: "factions" },
  { id: "faction-proposals", label: "Faction Proposals", icon: Plus, perm: "factions" },
  { id: "music", label: "Music", icon: Music, perm: "music" },
  { id: "rules", label: "Rules", icon: BookOpen, superAdminOnly: true },
  { id: "guide", label: "Guide", icon: Compass, superAdminOnly: true },
  { id: "media", label: "Media", icon: Camera, superAdminOnly: true },
  {
    id: "mod-tracker",
    label: "Mod Tracker",
    icon: Package,
    href: "/modtracker",
    // Either the full edit or the tester perm unlocks the link — the
    // Mod Tracker page itself further restricts what testers can edit.
    perms: ["mod_tracker_edit", "mod_tracker_tester"],
  },
  { id: "staff", label: "Staff", icon: Users, superAdminOnly: true },
];

function tabVisibleTo(tab, { isSuperAdmin, permissions }) {
  if (isSuperAdmin) return true;
  if (tab.superAdminOnly) return false;
  if (tab.perms) return tab.perms.some((p) => permissions.includes(p));
  if (tab.perm) return permissions.includes(tab.perm);
  return true; // fallback: any admin
}

const STATUS_TABS = [
  { id: "pending", label: "Pending" },
  { id: "approved", label: "Approved" },
  { id: "rejected", label: "Rejected" },
];

const STATUS_STYLES = {
  pending: "border-[#C0A875] text-[#C0A875]",
  approved: "border-[#4ADE80] text-[#4ADE80]",
  rejected: "border-[#C0392B] text-[#C0392B]",
};

export default function AdminPage() {
  const { loaded, isConnected, isAdmin, isSuperAdmin, permissions, user, connect } = useDiscord();

  // Which top-level tabs this user is allowed to see. Super-admins get all;
  // staff members get exactly the tabs matching their `permissions` list.
  const visibleTabs = useMemo(
    () => ADMIN_TABS.filter((t) => tabVisibleTo(t, { isSuperAdmin, permissions })),
    [isSuperAdmin, permissions]
  );
  // The first tab that's rendered inline (not an external `href` link).
  const firstInternalTab = visibleTabs.find((t) => !t.href)?.id || null;
  const [section, setSection] = useState(firstInternalTab || "applications");
  // Keep `section` valid when the visibility set changes (e.g. after login).
  useEffect(() => {
    if (firstInternalTab && !visibleTabs.some((t) => !t.href && t.id === section)) {
      setSection(firstInternalTab);
    }
  }, [firstInternalTab, visibleTabs, section]);

  if (!loaded) {
    return (
      <Shell>
        <CenterCard>
          <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={28} />
          <p className="mt-4 label-tag">Loading session…</p>
        </CenterCard>
      </Shell>
    );
  }

  if (!isConnected) {
    return (
      <Shell>
        <CenterCard>
          <ShieldAlert className="text-[#C0A875] mx-auto" size={36} />
          <h2 className="mt-4 font-display uppercase text-[#E2E4DF] text-3xl">Admin only</h2>
          <p className="mt-2 text-[#8D9A87] text-sm">Sign in with Discord to continue.</p>
          <button
            data-testid="admin-connect-button"
            onClick={connect}
            className="mt-6 bg-[#5865F2] hover:bg-[#4752C4] text-white px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
          >
            Connect Discord
          </button>
        </CenterCard>
      </Shell>
    );
  }

  if (!isAdmin) {
    return (
      <Shell>
        <CenterCard>
          <ShieldAlert className="text-[#C0392B] mx-auto" size={36} />
          <h2 className="mt-4 font-display uppercase text-[#E2E4DF] text-3xl">Access denied</h2>
          <p className="mt-2 text-[#8D9A87] text-sm max-w-md mx-auto">
            You're signed in as <span className="text-[#C0A875]">@{user?.username}</span> (ID{" "}
            <span className="font-mono-ui">{user?.id}</span>), but this Discord account isn't on the admin list.
          </p>
          <Link
            to="/"
            className="mt-6 inline-block border border-[#5C6B51] hover:border-[#C0A875] text-[#E2E4DF] hover:text-[#C0A875] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em]"
          >
            ← Back to site
          </Link>
        </CenterCard>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="py-20 sm:py-24 border-t border-[#283524]">
        <div className="max-w-6xl mx-auto px-6">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <span className="label-tag">// SURVIVORS' REGISTRY</span>
              <h1 className="mt-3 font-display uppercase text-[#E2E4DF] text-4xl sm:text-5xl">
                Admin Console
              </h1>
            </div>
            <p className="text-[#8D9A87] text-sm font-mono-ui">
              Logged in as <span className="text-[#C0A875]">@{user?.username}</span>
            </p>
          </div>

          {/* Top-level admin sections */}
          <div className="mt-10 flex flex-wrap gap-2 border-b border-[#283524]">
            {visibleTabs.map((t) => {
              const Icon = t.icon;
              const active = section === t.id;
              const cls = `inline-flex items-center gap-2 px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[#C0A875] text-[#C0A875]"
                  : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
              }`;
              // Tabs with `href` navigate to another route instead of switching
              // the internal `section` state — used by the Mod Tracker page.
              if (t.href) {
                return (
                  <Link
                    key={t.id}
                    to={t.href}
                    data-testid={`admin-section-${t.id}`}
                    className={cls}
                  >
                    <Icon size={14} />
                    {t.label}
                  </Link>
                );
              }
              return (
                <button
                  key={t.id}
                  data-testid={`admin-section-${t.id}`}
                  onClick={() => setSection(t.id)}
                  className={cls}
                >
                  <Icon size={14} />
                  {t.label}
                </button>
              );
            })}
          </div>

          {/* Section content — hidden if the user has no in-page tabs (e.g.
              a Tester whose only allowed destination is the Mod Tracker link). */}
          {!firstInternalTab ? (
            <div className="mt-16 max-w-md mx-auto text-center border border-[#283524] bg-[#151B14] p-8">
              <Package className="text-[#C0A875] mx-auto" size={36} />
              <h2 className="mt-4 font-display uppercase text-[#E2E4DF] text-2xl">Mod Tracker</h2>
              <p className="mt-2 text-[#8D9A87] text-sm">
                You have tester access. Head over to the Mod Tracker to update mod statuses.
              </p>
              <Link
                to="/modtracker"
                data-testid="admin-tester-goto-modtracker"
                className="mt-6 inline-block bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-6 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold"
              >
                Open Mod Tracker →
              </Link>
            </div>
          ) : null}

          {/* Section content — only rendered when the current `section` is in
              the caller's visible tabs, so a tester whose URL-set section
              pointed at a hidden tab never fires that section's API calls. */}
          {firstInternalTab && visibleTabs.some((t) => !t.href && t.id === section) && (
            <div className="mt-10">
              {section === "applications" && <ApplicationsSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "site" && <SiteSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "members" && <MembersSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "factions" && <FactionsSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "faction-proposals" && <FactionProposalsSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "music" && <MusicSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "rules" && <RulesSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "guide" && <GuideSection isSuperAdmin={!!user?.is_super_admin} />}
              {section === "media" && <MediaSection />}
              {section === "staff" && <StaffSection isSuperAdmin={!!user?.is_super_admin} />}
            </div>
          )}
        </div>
      </section>
    </Shell>
  );
}

function SiteSection({ isSuperAdmin }) {
  const [sub, setSub] = useState("announcement");
  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-8 text-center">
        <ShieldAlert className="text-[#C0392B] mx-auto mb-3" size={20} />
        <p className="font-mono-ui text-xs uppercase tracking-[0.2em] text-[#8D9A87]">
          // Super-admin only. Ask a super-admin to open the site editor.
        </p>
      </div>
    );
  }
  const tabs = [
    { id: "announcement", label: "Announcement" },
    { id: "home", label: "Home Page" },
    { id: "features", label: "Site Editor" },
    { id: "lore", label: "Lore Page" },
  ];
  return (
    <div data-testid="admin-site-section">
      <div className="mb-6 flex flex-wrap gap-1 border-b border-[#283524]">
        {tabs.map((t) => {
          const active = sub === t.id;
          return (
            <button
              key={t.id}
              type="button"
              onClick={() => setSub(t.id)}
              data-testid={`site-subtab-${t.id}`}
              className={`px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
                active
                  ? "border-[#C0A875] text-[#C0A875]"
                  : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
              }`}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {sub === "announcement" && <AnnouncementEditor />}
      {sub === "home" && <HomeEditor />}
      {sub === "features" && <FeaturesEditor />}
      {sub === "lore" && <LoreEditor />}
    </div>
  );
}

function ApplicationsSection({ isSuperAdmin }) {
  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async (s) => {
    setLoading(true);
    try {
      const res = await api.get(`/whitelist/applications?status=${s}`);
      setItems(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Failed to load applications.";
      toast.error(msg);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(status);
  }, [status, load]);

  const updateStatus = async (id, next) => {
    try {
      await api.patch(`/whitelist/applications/${id}`, { status: next });
      toast.success(`Application ${next}.`);
      setOpenId(null);
      load(status);
    } catch (err) {
      const msg = err?.response?.data?.detail || "Update failed.";
      toast.error(msg);
    }
  };

  const deleteApp = async (id) => {
    if (!window.confirm("Permanently delete this application? This cannot be undone. (Membership for approved survivors stays intact.)")) return;
    try {
      await api.delete(`/whitelist/applications/${id}`);
      toast.success("Application deleted.");
      setOpenId(null);
      load(status);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div>
      {isSuperAdmin && <ServerStatsEditor />}
      {isSuperAdmin && <SubmitFailuresPanel />}
      {/* Status filter */}
      <div className="flex flex-wrap gap-1 border-b border-[#283524]">
        {STATUS_TABS.map((t) => (
          <button
            key={t.id}
            data-testid={`tab-${t.id}`}
            onClick={() => setStatus(t.id)}
            className={`px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              status === t.id
                ? "border-[#C0A875] text-[#C0A875]"
                : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="mt-8 space-y-3">
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
          </div>
        )}
        {!loading && items.length === 0 && (
          <p className="text-[#8D9A87] text-sm font-mono-ui">// No applications in this bucket.</p>
        )}
        {!loading &&
          items.map((app) => (
            <ApplicationRow
              key={app.id}
              app={app}
              expanded={openId === app.id}
              isSuperAdmin={isSuperAdmin}
              onToggle={() => setOpenId(openId === app.id ? null : app.id)}
              onApprove={() => updateStatus(app.id, "approved")}
              onReject={() => updateStatus(app.id, "rejected")}
              onReset={() => updateStatus(app.id, "pending")}
              onDelete={() => deleteApp(app.id)}
            />
          ))}
      </div>
    </div>
  );
}

function ApplicationRow({ app, expanded, onToggle, onApprove, onReject, onReset, onDelete, onCredentialsSaved, isSuperAdmin }) {
  const submitted = new Date(app.submitted_at).toLocaleString();
  const [whitelisted, setWhitelisted] = useState(!!app.whitelisted);
  const [savingWl, setSavingWl] = useState(false);

  // Keep local state in sync when the parent reloads the app doc.
  useEffect(() => { setWhitelisted(!!app.whitelisted); }, [app.whitelisted, app.id]);

  const toggleWhitelisted = async (e) => {
    e.stopPropagation(); // don't collapse/expand the row when clicking the checkbox
    const next = !whitelisted;
    setSavingWl(true);
    // Optimistic — flip immediately, rollback on error.
    setWhitelisted(next);
    try {
      await api.patch(`/whitelist/applications/${app.id}/whitelisted`, { whitelisted: next });
      toast.success(next ? "Marked as added to whitelist." : "Un-marked whitelist status.");
      onCredentialsSaved?.(); // reuse parent's reload
    } catch (err) {
      setWhitelisted(!next); // rollback
      toast.error(err?.response?.data?.detail || "Update failed.");
    } finally {
      setSavingWl(false);
    }
  };
  return (
    <div
      data-testid={`app-row-${app.id}`}
      className="bg-[#161D14] border border-[#283524] hover:border-[#5C6B51] transition-colors"
    >
      <button
        onClick={onToggle}
        className="w-full flex flex-wrap items-center gap-4 p-5 text-left"
      >
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {app.discord_avatar ? (
            <img src={app.discord_avatar} alt="" className="w-10 h-10 rounded-full border border-[#283524] shrink-0" />
          ) : (
            <div className="w-10 h-10 border border-[#283524] flex items-center justify-center font-display text-[#C0A875] shrink-0">
              {(app.character_name || "?")[0]}
            </div>
          )}
          <div className="min-w-0">
            <div className="font-display uppercase text-[#E2E4DF] text-lg leading-tight truncate">
              {app.character_name}
            </div>
            <div className="font-mono-ui text-[11px] text-[#8D9A87] truncate">
              @{app.discord_username} · {submitted}
            </div>
          </div>
        </div>
        <ThoroughnessDot app={app} />
        <DmStatusChip app={app} />
        {app.status === "approved" && (
          <span
            role="checkbox"
            tabIndex={0}
            aria-checked={whitelisted}
            aria-busy={savingWl}
            onClick={toggleWhitelisted}
            onKeyDown={(e) => {
              if (e.key === " " || e.key === "Enter") {
                e.preventDefault();
                toggleWhitelisted(e);
              }
            }}
            data-testid={`whitelisted-toggle-${app.id}`}
            title={whitelisted ? "Marked as added to server whitelist — click to un-mark" : "Not yet added to server whitelist — click to mark done"}
            className={`inline-flex items-center gap-2 px-3 py-1.5 border font-mono-ui text-[10px] uppercase tracking-[0.25em] cursor-pointer select-none transition-colors ${
              whitelisted
                ? "border-emerald-500/50 bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20"
                : "border-[#283524] bg-[#0D110C] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875]"
            } ${savingWl ? "opacity-60 pointer-events-none" : ""}`}
          >
            <span
              className={`w-3.5 h-3.5 border flex items-center justify-center shrink-0 ${
                whitelisted ? "border-emerald-400 bg-emerald-500/30" : "border-[#5C6B51]"
              }`}
            >
              {whitelisted && <CheckCircle2 size={10} className="text-emerald-300" />}
            </span>
            {whitelisted ? "Whitelisted" : "Not on whitelist"}
          </span>
        )}
        <span
          className={`px-3 py-1.5 border font-mono-ui text-[10px] uppercase tracking-[0.25em] ${
            STATUS_STYLES[app.status] || "border-[#283524] text-[#8D9A87]"
          }`}
        >
          {app.status}
        </span>
      </button>

      {expanded && (
        <div className="border-t border-[#283524] p-6 sm:p-8 space-y-5 bg-[#0D110C]">
          <Grid>
            <Detail label="DOB" value={formatDob(app.dob)} />
            <Detail label="Timezone" value={app.timezone} />
            <Detail label="PZ Hours" value={String(app.hours_played)} />
            <Detail label="RP Experience" value={app.rp_experience} />
            <Detail
              label="Semi-Serious RP?"
              value={app.understands_semi_serious_rp === true ? "Yes" : app.understands_semi_serious_rp === false ? "No" : "—"}
            />
            <Detail label="Voice OK" value={app.voice_chat_ok ? "Yes" : "No"} />
            <Detail label="Referred by" value={app.referred_by || "—"} />
            <Detail label="Discord ID" value={app.discord_id} mono />
          </Grid>

          {isSuperAdmin && (
            <ServerCredentialsBlock
              appId={app.id}
              username={app.server_username}
              password={app.server_password}
              canEdit={isSuperAdmin}
              onSaved={onCredentialsSaved}
            />
          )}

          <Block label="Backstory" value={app.backstory} />
          <Block label="Scenario response" value={app.scenario_response} />
          <Block label="Why this server?" value={app.why_join} />

          {app.reviewed_at && (
            <p className="font-mono-ui text-[11px] text-[#8D9A87]">
              Reviewed {new Date(app.reviewed_at).toLocaleString()}
            </p>
          )}

          <div className="pt-4 border-t border-[#283524] flex flex-wrap gap-3">
            {app.status !== "approved" && (
              <button
                data-testid={`approve-${app.id}`}
                onClick={onApprove}
                className="inline-flex items-center gap-2 bg-[#4ADE80]/15 border border-[#4ADE80] text-[#4ADE80] hover:bg-[#4ADE80]/25 px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.2em]"
              >
                <CheckCircle2 size={14} /> Approve
              </button>
            )}
            {app.status === "pending" && (
              <button
                data-testid={`reject-${app.id}`}
                onClick={onReject}
                className="inline-flex items-center gap-2 bg-[#C0392B]/15 border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/25 px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.2em]"
              >
                <XCircle size={14} /> Reject
              </button>
            )}
            {app.status === "rejected" && (
              <button
                data-testid={`reset-${app.id}`}
                onClick={onReset}
                className="inline-flex items-center gap-2 border border-[#283524] text-[#8D9A87] hover:text-[#E2E4DF] hover:border-[#5C6B51] px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.2em]"
              >
                <RotateCcw size={14} /> Reset to pending
              </button>
            )}
            {(app.status === "approved" || app.status === "rejected") && (
              <button
                data-testid={`delete-${app.id}`}
                onClick={onDelete}
                className="inline-flex items-center gap-2 ml-auto border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-5 py-2.5 font-mono-ui text-xs uppercase tracking-[0.2em]"
              >
                <Trash2 size={14} /> Delete
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function Shell({ children }) {
  return (
    <div className="App min-h-screen">
      <Navbar />
      <main className="pt-[72px]">{children}</main>
      <Footer />
    </div>
  );
}
function CenterCard({ children }) {
  return (
    <section className="py-32">
      <div className="max-w-md mx-auto px-6 text-center bg-[#161D14] border border-[#283524] p-10">
        {children}
      </div>
    </section>
  );
}
function Grid({ children }) {
  return <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">{children}</div>;
}
function Detail({ label, value, mono = false }) {
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      <div className={`text-[#E2E4DF] text-sm ${mono ? "font-mono-ui break-all" : ""}`}>{value}</div>
    </div>
  );
}

/**
 * Convert an ISO-ish DOB string (YYYY-MM-DD as stored by <input type="date">)
 * into the US-friendly MM-DD-YYYY admins read more naturally.
 */
function formatDob(raw) {
  if (!raw) return "—";
  const m = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) return `${m[2]}-${m[3]}-${m[1]}`;
  return raw;
}
function Block({ label, value }) {
  return (
    <div>
      <div className="label-tag mb-2">{label}</div>
      <p
        className="text-[#E2E4DF] text-sm leading-relaxed whitespace-pre-wrap"
        style={{ overflowWrap: "anywhere", wordBreak: "break-word" }}
      >
        {value || "—"}
      </p>
    </div>
  );
}


/**
 * Tiny coloured dot scoring how thorough an application is, based on how
 * close the writing fields got to their suggested character counts.
 *  - green  ≥ 80% of the suggested budget
 *  - yellow 40 – 79%
 *  - red    < 40%
 */
function ThoroughnessDot({ app }) {
  const targets = [
    { len: (app.backstory || "").length, target: 300 },
    { len: (app.scenario_response || "").length, target: 200 },
    { len: (app.why_join || "").length, target: 80 },
  ];
  const score =
    targets.reduce((acc, t) => acc + Math.min(1, t.len / t.target), 0) / targets.length;
  let color = "#C0392B"; // red
  let label = "Thin application";
  if (score >= 0.8) {
    color = "#9CC279";
    label = "Thorough application";
  } else if (score >= 0.4) {
    color = "#C0A875";
    label = "Decent application";
  }
  return (
    <span
      data-testid={`thoroughness-${app.id}`}
      title={`${label} · ${Math.round(score * 100)}% of suggested length`}
      className="inline-flex items-center gap-2"
    >
      <span
        className="w-2.5 h-2.5 rounded-full shrink-0"
        style={{ background: color, boxShadow: `0 0 8px ${color}55` }}
      />
    </span>
  );
}

function ServerCredentialsBlock({ appId, username, password, canEdit = false, onSaved }) {
  const [shown, setShown] = useState(false);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [u, setU] = useState(username || "");
  const [p, setP] = useState(password || "");

  const save = async () => {
    const nextU = u.trim();
    const nextP = p.trim();
    if (!nextU && !nextP) {
      toast.error("Enter a username or password first.");
      return;
    }
    setSaving(true);
    try {
      await api.patch(`/whitelist/applications/${appId}/credentials`, {
        server_username: nextU || undefined,
        server_password: nextP || undefined,
      });
      toast.success("Credentials saved.");
      setEditing(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!username && !password && !editing) {
    return (
      <div className="border border-dashed border-[#283524] bg-[#0F1410] p-4">
        <div className="flex items-center justify-between gap-3 mb-2">
          <div className="label-tag">Server credentials</div>
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid={`cred-add-${appId}`}
              className="inline-flex items-center gap-1.5 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-2.5 py-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] transition-colors"
            >
              <Plus size={11} /> Add credentials
            </button>
          )}
        </div>
        <p className="font-mono-ui text-xs text-[#8D9A87]">
          // Legacy application — submitted before credential collection.
        </p>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="border border-[#C0A875]/40 bg-[#C0A875]/5 p-4">
        <div className="flex items-center justify-between mb-3">
          <div className="label-tag">Set server credentials</div>
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0A875]">Super-admin</span>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block label-tag mb-1">Username</label>
            <input value={u} onChange={(e) => setU(e.target.value)} data-testid={`cred-input-username-${appId}`}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none" />
          </div>
          <div>
            <label className="block label-tag mb-1">Password</label>
            <input value={p} onChange={(e) => setP(e.target.value)} data-testid={`cred-input-password-${appId}`}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none" />
          </div>
        </div>
        <div className="mt-3 flex items-center justify-end gap-2">
          <button type="button" onClick={() => { setEditing(false); setU(username || ""); setP(password || ""); }}
            className="border border-[#5C6B51] text-[#8D9A87] hover:text-[#E2E4DF] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em]">
            Cancel
          </button>
          <button type="button" onClick={save} disabled={saving} data-testid={`cred-save-${appId}`}
            className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] font-semibold">
            {saving ? "Saving…" : "Save credentials"}
          </button>
        </div>
      </div>
    );
  }

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied to clipboard.`);
    } catch {
      toast.error("Couldn't copy — select & copy manually.");
    }
  };
  return (
    <div className="border border-[#C0A875]/40 bg-[#C0A875]/5 p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="label-tag">Server credentials (paste into PZ whitelist)</div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <button
              type="button"
              onClick={() => setEditing(true)}
              data-testid={`cred-edit-${appId}`}
              title="Edit credentials"
              className="w-6 h-6 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] flex items-center justify-center"
            >
              <Pencil size={11} />
            </button>
          )}
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0A875]">
            Admin only
          </span>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <div className="bg-[#0D110C] border border-[#283524] p-3">
          <div className="label-tag mb-1">Username</div>
          <div className="flex items-center gap-2">
            <code data-testid="cred-username" className="flex-1 font-mono-ui text-sm text-[#E2E4DF] break-all">
              {username || "—"}
            </code>
            <button
              type="button"
              onClick={() => copy(username, "Username")}
              data-testid="cred-copy-username"
              className="p-1.5 text-[#8D9A87] hover:text-[#C0A875]"
              title="Copy username"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
        <div className="bg-[#0D110C] border border-[#283524] p-3">
          <div className="label-tag mb-1">Password</div>
          <div className="flex items-center gap-2">
            <code
              data-testid="cred-password"
              className="flex-1 font-mono-ui text-sm text-[#E2E4DF] break-all"
              style={{ WebkitTextSecurity: shown ? "none" : "disc" }}
            >
              {shown ? (password || "—") : "•".repeat(Math.min((password || "").length || 8, 12))}
            </code>
            <button
              type="button"
              onClick={() => setShown((v) => !v)}
              data-testid="cred-toggle-password"
              className="p-1.5 text-[#8D9A87] hover:text-[#C0A875]"
              title={shown ? "Hide password" : "Reveal password"}
            >
              {shown ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
            <button
              type="button"
              onClick={() => copy(password, "Password")}
              data-testid="cred-copy-password"
              className="p-1.5 text-[#8D9A87] hover:text-[#C0A875]"
              title="Copy password"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}


function StaffSection({ isSuperAdmin }) {
  const [staff, setStaff] = useState([]);
  const [superAdmins, setSuperAdmins] = useState([]);
  const [availablePerms, setAvailablePerms] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newId, setNewId] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newPerms, setNewPerms] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/staff");
      setStaff(res.data.staff || []);
      setSuperAdmins(res.data.super_admins || []);
      setAvailablePerms(res.data.available_permissions || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load staff.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const addStaff = async (e) => {
    e.preventDefault();
    if (!newId.trim()) return;
    setSubmitting(true);
    try {
      await api.post("/admin/staff", {
        discord_id: newId.trim(),
        label: newLabel.trim() || null,
        permissions: newPerms,
      });
      toast.success("Admin added.");
      setNewId("");
      setNewLabel("");
      setNewPerms([]);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to add admin.");
    } finally {
      setSubmitting(false);
    }
  };

  const removeStaff = async (id) => {
    if (!window.confirm("Remove this admin?")) return;
    try {
      await api.delete(`/admin/staff/${id}`);
      toast.success("Admin removed.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to remove admin.");
    }
  };

  const updatePermissions = async (id, permissions) => {
    try {
      await api.patch(`/admin/staff/${id}`, { permissions });
      toast.success("Permissions updated.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update permissions.");
    }
  };

  const updateLabel = async (id, label) => {
    try {
      await api.patch(`/admin/staff/${id}`, { label });
      toast.success("Position updated.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to update position.");
    }
  };

  const togglePerm = (perm) =>
    setNewPerms((prev) =>
      prev.includes(perm) ? prev.filter((p) => p !== perm) : [...prev, perm]
    );

  return (
    <div className="space-y-8">
      {/* Add form (super-admin only) */}
      {isSuperAdmin ? (
        <form
          onSubmit={addStaff}
          data-testid="staff-add-form"
          className="bg-[#161D14] border border-[#283524] p-6 sm:p-8"
        >
          <h3 className="font-display uppercase text-[#E2E4DF] text-xl">Add admin</h3>
          <p className="mt-1 text-[#8D9A87] text-sm">
            Promote another Discord user and choose which areas they can manage.
          </p>
          <div className="mt-5 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div className="sm:col-span-1">
              <label className="block label-tag mb-2 text-[#E2E4DF] font-bold">Discord User ID *</label>
              <input
                data-testid="staff-id-input"
                value={newId}
                onChange={(e) => setNewId(e.target.value.replace(/\D/g, ""))}
                placeholder="123456789012345678"
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-4 py-3 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
                required
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block label-tag mb-2 text-[#E2E4DF] font-bold">Position (optional)</label>
              <input
                data-testid="staff-label-input"
                value={newLabel}
                onChange={(e) => setNewLabel(e.target.value)}
                placeholder="e.g. Head Mod, Whitelist Lead"
                maxLength={48}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-4 py-3 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
              />
            </div>
          </div>
          {/* Permissions grid */}
          {availablePerms.length > 0 && (
            <div className="mt-5">
              <label className="block label-tag mb-2 text-[#E2E4DF] font-bold">Permissions</label>
              <div
                className="grid grid-cols-2 sm:grid-cols-4 gap-2"
                data-testid="staff-add-permissions"
              >
                {availablePerms.map((p) => {
                  const checked = newPerms.includes(p);
                  return (
                    <button
                      key={p}
                      type="button"
                      data-testid={`staff-add-perm-${p}`}
                      onClick={() => togglePerm(p)}
                      className={`px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] border transition-colors text-left ${
                        checked
                          ? "bg-[#C0A875]/15 border-[#C0A875] text-[#C0A875]"
                          : "border-[#283524] text-[#8D9A87] hover:border-[#5C6B51]"
                      }`}
                    >
                      {checked ? "✓ " : ""}
                      {PERM_LABELS[p] || p}
                    </button>
                  );
                })}
              </div>
              <p className="mt-2 text-[#8D9A87] text-[11px] font-mono-ui">
                // Delete of applications remains super-admin only regardless.
              </p>
            </div>
          )}
          <p className="mt-3 text-[#A8B5A0] text-[13px] leading-relaxed">
            Tip: in Discord, right-click a user → Copy User ID (requires Developer Mode in your Discord
            settings).
          </p>
          <button
            type="submit"
            data-testid="staff-add-submit"
            disabled={submitting || !newId.trim()}
            className="mt-5 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-6 py-2.5 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="animate-spin" size={14} />}
            Add Admin →
          </button>
        </form>
      ) : (
        <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
          <span className="font-mono-ui">// Only the server owner can add or remove admins.</span>
        </div>
      )}

      {/* List */}
      <div>
        <h3 className="font-display uppercase text-[#E2E4DF] text-xl mb-4">Current admins</h3>
        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={20} />
          </div>
        ) : (
          <div className="space-y-2" data-testid="staff-list">
            {superAdmins.map((s) => (
              <StaffRow
                key={s.discord_id}
                entry={s}
                canRemove={false}
                canEditPerms={false}
                availablePerms={availablePerms}
              />
            ))}
            {staff.length === 0 && superAdmins.length === 0 && (
              <p className="text-[#8D9A87] text-sm font-mono-ui">// No admins on file.</p>
            )}
            {staff.map((s) => (
              <StaffRow
                key={s.discord_id}
                entry={s}
                canRemove={isSuperAdmin}
                canEditPerms={isSuperAdmin}
                canEditLabel={isSuperAdmin}
                availablePerms={availablePerms}
                onRemove={() => removeStaff(s.discord_id)}
                onPermsChange={(perms) => updatePermissions(s.discord_id, perms)}
                onLabelChange={(label) => updateLabel(s.discord_id, label)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const PERM_LABELS = {
  applications: "Applications",
  members: "Members",
  factions: "Factions",
  music: "Music",
  mod_tracker_edit: "Mod Tracker (full edit)",
  mod_tracker_tester: "Mod Tracker — Tester (status only)",
};

function StaffRow({ entry, canRemove, canEditPerms, canEditLabel, availablePerms, onRemove, onPermsChange, onLabelChange }) {
  const [perms, setPerms] = useState(entry.permissions || []);
  const [dirty, setDirty] = useState(false);
  const [editingLabel, setEditingLabel] = useState(false);
  const [labelDraft, setLabelDraft] = useState(entry.label || "");

  useEffect(() => {
    setPerms(entry.permissions || []);
    setDirty(false);
    setLabelDraft(entry.label || "");
    setEditingLabel(false);
  }, [entry.permissions, entry.label, entry.discord_id]);

  const toggle = (p) => {
    setPerms((prev) => {
      const next = prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p];
      setDirty(true);
      return next;
    });
  };

  const save = () => {
    onPermsChange?.(perms);
    setDirty(false);
  };

  const saveLabel = () => {
    const next = labelDraft.trim();
    if (next === (entry.label || "").trim()) {
      setEditingLabel(false);
      return;
    }
    onLabelChange?.(next);
    setEditingLabel(false);
  };

  const cancelLabel = () => {
    setLabelDraft(entry.label || "");
    setEditingLabel(false);
  };

  const handle = entry.discord_username ? `@${entry.discord_username}` : "@—";
  const website = entry.website_username || "—";
  const position = entry.label || (entry.is_super_admin ? "Server Owner" : "Admin");

  return (
    <div
      data-testid={`staff-row-${entry.discord_id}`}
      className="p-4 bg-[#161D14] border border-[#283524] hover:border-[#5C6B51] transition-colors"
    >
      <div className="flex items-center gap-4">
        <div
          className={`w-9 h-9 border flex items-center justify-center shrink-0 ${
            entry.is_super_admin ? "border-[#C0A875] text-[#C0A875]" : "border-[#283524] text-[#8D9A87]"
          }`}
        >
          {entry.is_super_admin ? <Crown size={16} /> : <Users size={16} />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-mono-ui text-sm text-[#E2E4DF] truncate flex flex-wrap items-baseline gap-x-2">
            <span
              className={entry.discord_username ? "text-[#C0A875]" : "text-[#5C6B51]"}
              data-testid={`staff-row-handle-${entry.discord_id}`}
            >
              {handle}
            </span>
            <span className="text-[#5C6B51]">|</span>
            <span className="text-[#E2E4DF]" data-testid={`staff-row-website-${entry.discord_id}`}>
              {website}
            </span>
            <span className="text-[#5C6B51]">|</span>
            {editingLabel ? (
              <span className="inline-flex items-center gap-1.5">
                <input
                  autoFocus
                  value={labelDraft}
                  onChange={(e) => setLabelDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") { e.preventDefault(); saveLabel(); }
                    if (e.key === "Escape") { e.preventDefault(); cancelLabel(); }
                  }}
                  placeholder="e.g. Head Mod"
                  data-testid={`staff-row-position-input-${entry.discord_id}`}
                  className="bg-[#0D110C] border border-[#C0A875] text-[#E2E4DF] px-2 py-1 font-mono-ui text-xs w-48 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={saveLabel}
                  data-testid={`staff-row-position-save-${entry.discord_id}`}
                  className="w-6 h-6 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 flex items-center justify-center"
                  title="Save (Enter)"
                >
                  <CheckCircle2 size={12} />
                </button>
                <button
                  type="button"
                  onClick={cancelLabel}
                  data-testid={`staff-row-position-cancel-${entry.discord_id}`}
                  className="w-6 h-6 border border-[#5C6B51] text-[#8D9A87] hover:text-[#E2E4DF] flex items-center justify-center"
                  title="Cancel (Esc)"
                >
                  <XCircle size={12} />
                </button>
              </span>
            ) : (
              <span className="text-[#E2E4DF] inline-flex items-center gap-1.5" data-testid={`staff-row-position-${entry.discord_id}`}>
                {position}
                {canEditLabel && (
                  <button
                    type="button"
                    onClick={() => setEditingLabel(true)}
                    title="Edit position"
                    data-testid={`staff-row-position-edit-${entry.discord_id}`}
                    className="w-5 h-5 text-[#5C6B51] hover:text-[#C0A875] flex items-center justify-center"
                  >
                    <Pencil size={10} />
                  </button>
                )}
              </span>
            )}
            <span className="text-[#5C6B51]">|</span>
            <span className="text-[#8D9A87] text-[11px]" data-testid={`staff-row-id-${entry.discord_id}`}>
              {entry.discord_id}
            </span>
          </div>
        </div>
        {entry.is_super_admin && (
          <span className="hidden sm:inline-block label-tag text-[#C0A875]">// SUPER ADMIN</span>
        )}
        {canRemove && (
          <button
            data-testid={`staff-remove-${entry.discord_id}`}
            onClick={onRemove}
            className="inline-flex items-center gap-2 border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
          >
            <Trash2 size={12} /> Remove
          </button>
        )}
      </div>

      {/* Permissions row */}
      {entry.is_super_admin ? (
        <p className="mt-3 pt-3 border-t border-[#283524] font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
          // Holds every permission automatically.
        </p>
      ) : (
        <div className="mt-3 pt-3 border-t border-[#283524]">
          <div className="flex items-center justify-between mb-2 gap-3">
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
              Permissions
            </span>
            {canEditPerms && dirty && (
              <button
                data-testid={`staff-perm-save-${entry.discord_id}`}
                onClick={save}
                className="inline-flex items-center gap-1.5 bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-3 py-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] font-semibold"
              >
                Save
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {availablePerms.map((p) => {
              const checked = perms.includes(p);
              return (
                <button
                  key={p}
                  type="button"
                  disabled={!canEditPerms}
                  data-testid={`staff-perm-${entry.discord_id}-${p}`}
                  onClick={() => toggle(p)}
                  className={`px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] border transition-colors text-left ${
                    checked
                      ? "bg-[#C0A875]/15 border-[#C0A875] text-[#C0A875]"
                      : "border-[#283524] text-[#8D9A87] hover:border-[#5C6B51]"
                  } ${canEditPerms ? "" : "cursor-not-allowed opacity-70"}`}
                >
                  {checked ? "✓ " : ""}
                  {PERM_LABELS[p] || p}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}


function MembersSection({ isSuperAdmin }) {
  const [active, setActive] = useState([]);
  const [graveyard, setGraveyard] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tab, setTab] = useState("active");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, g] = await Promise.all([
        api.get("/admin/members"),
        api.get("/admin/graveyard"),
      ]);
      setActive(a.data.members || []);
      setGraveyard(g.data.graveyard || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load members.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const archive = async (m) => {
    if (
      !window.confirm(
        `Archive ${m.character_name || m.discord_username} as K.I.A.?\n\n• They'll be moved to the Graveyard.\n• Their approved application is cleared so they can apply again with a brand-new character.`
      )
    )
      return;
    try {
      await api.post(`/admin/members/${m.discord_id}/archive`);
      toast.success("Character archived to the Graveyard.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Archive failed.");
    }
  };

  const toggleLock = async (g) => {
    try {
      const res = await api.patch(`/admin/graveyard/${g.id}/lock`);
      toast.success(res.data?.locked ? "Locked." : "Unlocked — you can edit now.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to toggle lock.");
    }
  };

  const saveEdit = async (g, updates) => {
    try {
      await api.patch(`/admin/graveyard/${g.id}`, updates);
      toast.success("Graveyard entry updated.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };

  const restore = async (g) => {
    if (
      !window.confirm(
        `Restore ${g.character_name || g.discord_username} from the Graveyard?\n\nThey'll be brought back as an active member. If they already have an active character on the wall, this will be blocked.`
      )
    )
      return;
    try {
      await api.post(`/admin/graveyard/${g.id}/restore`);
      toast.success("Character restored to the active roster.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed.");
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can archive characters.</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-wrap gap-1 border-b border-[#283524]">
        {[
          { id: "active", label: `Active (${active.length})` },
          { id: "graveyard", label: `Graveyard (${graveyard.length})` },
        ].map((t) => (
          <button
            key={t.id}
            data-testid={`members-tab-${t.id}`}
            onClick={() => setTab(t.id)}
            className={`px-5 py-3 font-mono-ui text-xs uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
              tab === t.id
                ? "border-[#C0A875] text-[#C0A875]"
                : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-3">
        {loading && (
          <div className="text-center py-12">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
          </div>
        )}
        {!loading && tab === "active" && active.length === 0 && (
          <p className="text-[#8D9A87] text-sm font-mono-ui">// No active members on the wall.</p>
        )}
        {!loading && tab === "active" &&
          active.map((m) => (
            <ActiveMemberRow key={m.discord_id} member={m} onArchive={() => archive(m)} onReload={load} />
          ))}

        {!loading && tab === "graveyard" && graveyard.length === 0 && (
          <p className="text-[#8D9A87] text-sm font-mono-ui">// The graveyard is empty.</p>
        )}
        {!loading && tab === "graveyard" &&
          graveyard.map((g) => (
            <GraveRow
              key={g.id}
              grave={g}
              onToggleLock={() => toggleLock(g)}
              onSaveEdit={(updates) => saveEdit(g, updates)}
              onRestore={() => restore(g)}
            />
          ))}
      </div>
    </div>
  );
}

function ActiveMemberRow({ member, onArchive, onReload }) {
  const charName = member.character_name || "Unnamed";
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    character_name: member.character_name || "",
    alias: member.alias || "",
    age: member.age ?? "",
    origin: member.origin || "",
    occupation: member.occupation || "",
    bio: member.bio || "",
    last_seen: member.last_seen || "",
  });
  const save = async () => {
    try {
      await api.patch(`/admin/members/${member.discord_id}`, {
        character_name: form.character_name.trim() || null,
        alias: form.alias.trim() || null,
        age: form.age === "" ? null : Number(form.age),
        origin: form.origin.trim() || null,
        occupation: form.occupation.trim() || null,
        bio: form.bio.trim() || null,
        last_seen: form.last_seen.trim() || null,
      });
      toast.success("Member updated.");
      setEditing(false);
      onReload && onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };
  return (
    <div
      data-testid={`active-member-${member.discord_id}`}
      className="bg-[#161D14] border border-[#283524] hover:border-[#5C6B51] transition-colors"
    >
      <div className="flex items-center gap-4 p-4">
        <div className="w-10 h-10 border border-[#283524] flex items-center justify-center overflow-hidden shrink-0">
          {member.avatar_data_url ? (
            <img src={member.avatar_data_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="font-display text-[#C0A875]">{charName[0]}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-[#E2E4DF] text-sm truncate">{charName}</div>
          <div className="font-mono-ui text-[11px] text-[#8D9A87] truncate">
            @{member.discord_username || "—"} · {(member.status || "alive").toUpperCase()}
          </div>
        </div>
        <button
          data-testid={`active-edit-${member.discord_id}`}
          onClick={() => setEditing((v) => !v)}
          className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
        >
          <Pencil size={12} /> {editing ? "Cancel" : "Edit"}
        </button>
        <button
          data-testid={`archive-${member.discord_id}`}
          onClick={onArchive}
          className="inline-flex items-center gap-2 border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
        >
          <Skull size={12} /> Archive K.I.A.
        </button>
      </div>

      {editing && (
        <div className="border-t border-[#283524] p-5 bg-[#0D110C] space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Mini label="Character name">
              <MiniInput value={form.character_name} onChange={(v) => setForm({ ...form, character_name: v })} />
            </Mini>
            <Mini label="Alias">
              <MiniInput value={form.alias} onChange={(v) => setForm({ ...form, alias: v })} />
            </Mini>
            <Mini label="Age">
              <MiniInput type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} />
            </Mini>
            <Mini label="Origin">
              <MiniInput value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} />
            </Mini>
            <Mini label="Occupation">
              <MiniInput value={form.occupation} onChange={(v) => setForm({ ...form, occupation: v })} />
            </Mini>
            <Mini label="Last seen">
              <MiniInput value={form.last_seen} onChange={(v) => setForm({ ...form, last_seen: v })} />
            </Mini>
          </div>
          <Mini label="Bio">
            <textarea
              data-testid={`active-edit-bio-${member.discord_id}`}
              rows={3}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
            />
          </Mini>
          <div className="flex justify-end">
            <button
              data-testid={`active-save-${member.discord_id}`}
              onClick={save}
              className="bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold"
            >
              Save →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function Mini({ label, children }) {
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      {children}
    </div>
  );
}

function MiniInput({ value, onChange, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
    />
  );
}

function GraveRow({ grave, onToggleLock, onSaveEdit, onRestore }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    character_name: grave.character_name || "",
    alias: grave.alias || "",
    age: grave.age ?? "",
    origin: grave.origin || "",
    occupation: grave.occupation || "",
    faction: grave.faction || "",
    bio: grave.bio || "",
    last_seen: grave.last_seen || "",
  });
  const locked = !!grave.locked;
  const fellOn = grave.archived_at ? new Date(grave.archived_at).toLocaleString() : "—";

  const submit = async () => {
    const updates = {
      character_name: form.character_name.trim() || null,
      alias: form.alias.trim() || null,
      age: form.age === "" ? null : Number(form.age),
      origin: form.origin.trim() || null,
      occupation: form.occupation.trim() || null,
      faction: form.faction.trim() || null,
      bio: form.bio.trim() || null,
      last_seen: form.last_seen.trim() || null,
    };
    await onSaveEdit(updates);
    setEditing(false);
  };

  return (
    <div
      data-testid={`grave-row-${grave.id}`}
      className="bg-[#0F1410] border border-[#1F2A1C]"
    >
      <div className="flex flex-wrap items-center gap-4 p-4">
        <div className="w-10 h-10 border border-[#1F2A1C] flex items-center justify-center overflow-hidden shrink-0">
          {grave.avatar_data_url ? (
            <img src={grave.avatar_data_url} alt="" className="w-full h-full object-cover grayscale" />
          ) : (
            <span className="font-display text-[#5C6B51]">{(grave.character_name || "?")[0]}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-[#A8B5A0] text-sm truncate">
            {grave.character_name || "Unnamed"}
          </div>
          <div className="font-mono-ui text-[11px] text-[#8D9A87] truncate">
            @{grave.discord_username || "—"} · Fell {fellOn}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-1.5 px-2 py-1 border font-mono-ui text-[10px] uppercase tracking-[0.2em] ${
            locked ? "border-[#5C6B51] text-[#8D9A87]" : "border-[#C0A875] text-[#C0A875]"
          }`}
        >
          {locked ? <Lock size={10} /> : <Unlock size={10} />}
          {locked ? "Locked" : "Unlocked"}
        </span>
        <button
          data-testid={`grave-toggle-lock-${grave.id}`}
          onClick={onToggleLock}
          className="inline-flex items-center gap-2 border border-[#5C6B51] text-[#A8B5A0] hover:border-[#C0A875] hover:text-[#C0A875] px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
        >
          {locked ? <Unlock size={12} /> : <Lock size={12} />}
          {locked ? "Unlock" : "Lock"}
        </button>
        {!locked && (
          <button
            data-testid={`grave-edit-${grave.id}`}
            onClick={() => setEditing((v) => !v)}
            className="inline-flex items-center gap-2 border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
          >
            <Pencil size={12} /> {editing ? "Cancel" : "Edit"}
          </button>
        )}
        <button
          data-testid={`grave-restore-${grave.id}`}
          onClick={onRestore}
          className="inline-flex items-center gap-2 border border-[#9CC279] text-[#9CC279] hover:bg-[#9CC279]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em]"
        >
          <Heart size={12} /> Restore
        </button>
      </div>

      {editing && !locked && (
        <div className="border-t border-[#1F2A1C] p-5 sm:p-6 space-y-4 bg-[#0D110C]">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <TinyField label="Character name">
              <TinyInput value={form.character_name} onChange={(v) => setForm({ ...form, character_name: v })} />
            </TinyField>
            <TinyField label="Alias">
              <TinyInput value={form.alias} onChange={(v) => setForm({ ...form, alias: v })} />
            </TinyField>
            <TinyField label="Age">
              <TinyInput type="number" value={form.age} onChange={(v) => setForm({ ...form, age: v })} />
            </TinyField>
            <TinyField label="Origin">
              <TinyInput value={form.origin} onChange={(v) => setForm({ ...form, origin: v })} />
            </TinyField>
            <TinyField label="Occupation">
              <TinyInput value={form.occupation} onChange={(v) => setForm({ ...form, occupation: v })} />
            </TinyField>
            <TinyField label="Faction">
              <TinyInput value={form.faction} onChange={(v) => setForm({ ...form, faction: v })} />
            </TinyField>
          </div>
          <TinyField label="Bio">
            <textarea
              data-testid={`grave-edit-bio-${grave.id}`}
              rows={4}
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
            />
          </TinyField>
          <TinyField label="Last seen">
            <TinyInput value={form.last_seen} onChange={(v) => setForm({ ...form, last_seen: v })} />
          </TinyField>
          <div className="flex justify-end">
            <button
              data-testid={`grave-save-${grave.id}`}
              onClick={submit}
              className="bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold"
            >
              Save →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function TinyField({ label, children }) {
  return (
    <div>
      <div className="label-tag mb-1">{label}</div>
      {children}
    </div>
  );
}

function TinyInput({ value, onChange, type = "text" }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
    />
  );
}


function FactionsSection({ isSuperAdmin }) {
  const [factions, setFactions] = useState([]);
  const [activeMembers, setActiveMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [draft, setDraft] = useState({ name: "", acronym: "", color: "", description: "", leader_discord_id: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [f, m] = await Promise.all([
        api.get("/factions"),
        api.get("/admin/members"),
      ]);
      setFactions(f.data.factions || []);
      setActiveMembers(m.data.members || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load factions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const createFaction = async () => {
    if (!draft.name.trim()) {
      toast.error("Name is required.");
      return;
    }
    setCreating(true);
    try {
      await api.post("/admin/factions", {
        name: draft.name.trim(),
        acronym: draft.acronym.trim() || null,
        color: draft.color || null,
        description: draft.description.trim() || null,
        leader_discord_id: draft.leader_discord_id || null,
      });
      toast.success("Faction created.");
      setDraft({ name: "", acronym: "", color: "", description: "", leader_discord_id: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally {
      setCreating(false);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can manage factions.</span>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="bg-[#161D14] border border-[#283524] p-6">
        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#C0A875] mb-4 inline-flex items-center gap-2">
          <Plus size={12} /> New faction
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <div className="label-tag mb-1">Name</div>
            <input
              data-testid="faction-new-name"
              value={draft.name}
              onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
              placeholder="e.g. Ones Who Live"
            />
          </div>
          <div>
            <div className="label-tag mb-1">Acronym (3–5 letters, dots allowed)</div>
            <input
              data-testid="faction-new-acronym"
              value={draft.acronym}
              onChange={(e) => setDraft({ ...draft, acronym: e.target.value.toUpperCase() })}
              maxLength={7}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs uppercase focus:border-[#C0A875] focus:outline-none"
              placeholder="e.g. O.W.L"
            />
          </div>
          <div className="sm:col-span-2">
            <div className="label-tag mb-2 flex items-center gap-3">
              <span>Colour</span>
              {(draft.acronym || draft.name) && (
                <FactionChip
                  faction={{ acronym: draft.acronym || draft.name.slice(0, 5), color: draft.color, name: draft.name }}
                  size="sm"
                  testid="faction-new-preview-chip"
                />
              )}
            </div>
            <ColorSwatchGrid
              value={draft.color}
              onChange={(c) => setDraft({ ...draft, color: c })}
              testidPrefix="faction-new-color"
            />
          </div>
          <div>
            <div className="label-tag mb-1">Initial leader (optional)</div>
            <select
              data-testid="faction-new-leader"
              value={draft.leader_discord_id}
              onChange={(e) => setDraft({ ...draft, leader_discord_id: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
            >
              <option value="">— No leader yet —</option>
              {activeMembers.map((m) => (
                <option key={m.discord_id} value={m.discord_id}>
                  {m.character_name || m.discord_username || m.discord_id}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <div className="label-tag mb-1">Description</div>
          <textarea
            data-testid="faction-new-description"
            value={draft.description}
            onChange={(e) => setDraft({ ...draft, description: e.target.value })}
            rows={3}
            className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
            placeholder="Optional one-liner shown on the faction picker."
          />
        </div>
        <div className="mt-5 flex justify-end">
          <button
            data-testid="faction-create-button"
            onClick={createFaction}
            disabled={creating}
            className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
          >
            {creating && <Loader2 className="animate-spin" size={12} />}
            Create faction
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87] mb-4">
          Factions ({factions.length})
        </h3>
        {loading && (
          <div className="text-center py-10">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={20} />
          </div>
        )}
        {!loading && factions.length === 0 && (
          <p className="text-[#8D9A87] text-sm font-mono-ui">// No factions yet. Add one above.</p>
        )}
        <div className="space-y-3">
          {factions.map((f) => (
            <FactionAdminRow
              key={f.id}
              faction={f}
              activeMembers={activeMembers}
              onReload={load}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function FactionAdminRow({ faction, activeMembers, onReload }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    name: faction.name,
    acronym: faction.acronym || "",
    color: faction.color || "",
    description: faction.description || "",
    leader_discord_id: faction.leader_discord_id || "",
  });

  const save = async () => {
    try {
      await api.patch(`/admin/factions/${faction.id}`, {
        name: form.name.trim(),
        acronym: form.acronym.trim() || "",
        color: form.color || "",
        description: form.description.trim() || "",
        leader_discord_id: form.leader_discord_id,
      });
      toast.success("Faction updated.");
      setEditing(false);
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Update failed.");
    }
  };

  const remove = async () => {
    if (
      !window.confirm(
        `Delete the "${faction.name}" faction?\n\nAll member references will be cleared. This cannot be undone.`
      )
    )
      return;
    try {
      await api.delete(`/admin/factions/${faction.id}`);
      toast.success("Faction deleted.");
      onReload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  return (
    <div className="bg-[#161D14] border border-[#283524]" data-testid={`faction-row-${faction.id}`}>
      <div className="flex flex-wrap items-center gap-4 p-4">
        {faction.color || faction.acronym ? (
          <FactionChip faction={faction} size="md" testid={`faction-chip-${faction.id}`} />
        ) : (
          <div className="w-10 h-10 border border-[#283524] flex items-center justify-center bg-[#0D110C]">
            <Flag className="text-[#C0A875]" size={16} />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <div className="font-display uppercase text-[#E2E4DF] text-sm truncate">{faction.name}</div>
          <div className="font-mono-ui text-[11px] text-[#8D9A87] truncate">
            Leader: {faction.leader_name || "—"} · {faction.member_count} member
            {faction.member_count === 1 ? "" : "s"}
          </div>
        </div>
        <button
          onClick={() => setEditing((v) => !v)}
          data-testid={`faction-edit-${faction.id}`}
          className="border border-[#C0A875] text-[#C0A875] hover:bg-[#C0A875]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
        >
          <Pencil size={12} /> {editing ? "Cancel" : "Edit"}
        </button>
        <button
          onClick={remove}
          data-testid={`faction-delete-${faction.id}`}
          className="border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-3 py-2 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-2"
        >
          <Trash2 size={12} /> Delete
        </button>
      </div>

      {editing && (
        <div className="border-t border-[#283524] bg-[#0D110C] p-5 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <div className="label-tag mb-1">Name</div>
              <input
                data-testid={`faction-edit-name-${faction.id}`}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
              />
            </div>
            <div>
              <div className="label-tag mb-1">Acronym</div>
              <input
                data-testid={`faction-edit-acronym-${faction.id}`}
                value={form.acronym}
                onChange={(e) => setForm({ ...form, acronym: e.target.value.toUpperCase() })}
                maxLength={7}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs uppercase focus:border-[#C0A875] focus:outline-none"
              />
            </div>
            <div className="sm:col-span-2">
              <div className="label-tag mb-2 flex items-center gap-3">
                <span>Colour</span>
                <FactionChip
                  faction={{ acronym: form.acronym || form.name.slice(0, 5), color: form.color, name: form.name }}
                  size="sm"
                />
              </div>
              <ColorSwatchGrid
                value={form.color}
                onChange={(c) => setForm({ ...form, color: c })}
                testidPrefix={`faction-edit-color-${faction.id}`}
              />
            </div>
            <div className="sm:col-span-2">
              <div className="label-tag mb-1">Leader</div>
              <select
                data-testid={`faction-edit-leader-${faction.id}`}
                value={form.leader_discord_id}
                onChange={(e) => setForm({ ...form, leader_discord_id: e.target.value })}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
              >
                <option value="">— No leader (vacant) —</option>
                {activeMembers.map((m) => (
                  <option key={m.discord_id} value={m.discord_id}>
                    {m.character_name || m.discord_username || m.discord_id}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <div className="label-tag mb-1">Description</div>
            <textarea
              data-testid={`faction-edit-description-${faction.id}`}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none resize-y"
            />
          </div>
          <div className="flex justify-end">
            <button
              data-testid={`faction-save-${faction.id}`}
              onClick={save}
              className="bg-[#C0A875] hover:bg-[#a99362] text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold"
            >
              Save →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function ColorSwatchGrid({ value, onChange, testidPrefix }) {
  return (
    <div
      data-testid={`${testidPrefix}-grid`}
      className="grid gap-1.5 p-2 bg-[#0D110C] border border-[#283524]"
      style={{ gridTemplateColumns: "repeat(auto-fill, minmax(28px, 1fr))" }}
    >
      <button
        type="button"
        title="No colour"
        data-testid={`${testidPrefix}-none`}
        onClick={() => onChange("")}
        className={`relative h-7 w-full border ${
          !value ? "border-[#C0A875]" : "border-[#283524]"
        } bg-[#161D14] hover:border-[#C0A875] transition-colors`}
      >
        <span
          className="absolute inset-0 flex items-center justify-center font-mono-ui text-[10px] text-[#8D9A87]"
        >
          ×
        </span>
      </button>
      {FACTION_COLORS.map((c) => {
        const selected = (value || "").toLowerCase() === c.toLowerCase();
        return (
          <button
            type="button"
            key={c}
            title={c}
            data-testid={`${testidPrefix}-${c.toLowerCase()}`}
            onClick={() => onChange(c)}
            style={{ background: c }}
            className={`h-7 w-full border-2 transition-all ${
              selected
                ? "border-[#E2E4DF] scale-110 z-10 relative shadow-[0_0_0_2px_#0D110C]"
                : "border-[#0D110C] hover:border-[#C0A875]"
            }`}
            aria-label={c}
          >
            {selected && (
              <span
                aria-hidden
                className="block w-full h-full"
                style={{
                  color: factionTextColor(c),
                  textAlign: "center",
                  lineHeight: "100%",
                  fontWeight: 900,
                  fontSize: "11px",
                  textShadow: "0 0 2px rgba(0,0,0,0.4)",
                }}
              >
                ✓
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}


function MusicSection({ isSuperAdmin }) {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("url"); // 'url' | 'upload'
  const [draft, setDraft] = useState({ title: "", artist: "", url: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/tracks");
      setTracks(res.data.tracks || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load tracks.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async () => {
    if (!draft.title.trim()) {
      toast.error("Song title is required.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "url") {
        if (!draft.url.trim()) {
          toast.error("Direct MP3/MP4 URL is required.");
          return;
        }
        await api.post("/admin/tracks", {
          title: draft.title.trim(),
          artist: draft.artist.trim() || null,
          url: draft.url.trim(),
        });
      } else {
        if (!file) {
          toast.error("Pick a file to upload.");
          return;
        }
        const fd = new FormData();
        fd.append("title", draft.title.trim());
        if (draft.artist.trim()) fd.append("artist", draft.artist.trim());
        fd.append("file", file);
        await api.post("/admin/tracks/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      toast.success("Track added.");
      setDraft({ title: "", artist: "", url: "" });
      setFile(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not add track.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"?`)) return;
    try {
      await api.delete(`/admin/tracks/${t.id}`);
      toast.success("Track deleted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can manage the music.</span>
      </div>
    );
  }

  return (
    <div className="space-y-10">
      <div className="bg-[#161D14] border border-[#283524] p-6">
        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#C0A875] mb-4 inline-flex items-center gap-2">
          <Plus size={12} /> Add a track
        </h3>

        <div className="flex gap-1 border-b border-[#283524] mb-5">
          {[
            { id: "url", label: "Direct URL" },
            { id: "upload", label: "Upload MP3" },
          ].map((t) => (
            <button
              key={t.id}
              data-testid={`music-mode-${t.id}`}
              onClick={() => setMode(t.id)}
              className={`px-4 py-2 font-mono-ui text-[10px] uppercase tracking-[0.25em] border-b-2 -mb-px transition-colors ${
                mode === t.id ? "border-[#C0A875] text-[#C0A875]" : "border-transparent text-[#8D9A87] hover:text-[#E2E4DF]"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="space-y-4">
          <div>
            <div className="label-tag mb-1">Song title <span className="text-[#C0392B]">*</span></div>
            <input
              data-testid="music-title-input"
              value={draft.title}
              onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
            />
          </div>
          <div>
            <div className="label-tag mb-1">Artist</div>
            <input
              data-testid="music-artist-input"
              value={draft.artist}
              onChange={(e) => setDraft({ ...draft, artist: e.target.value })}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
            />
          </div>
          {mode === "url" ? (
            <div>
              <div className="label-tag mb-1">Direct MP3/MP4 URL <span className="text-[#C0392B]">*</span></div>
              <input
                data-testid="music-url-input"
                value={draft.url}
                onChange={(e) => setDraft({ ...draft, url: e.target.value })}
                placeholder="https://example.com/track.mp3"
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
              />
              <p className="mt-1 font-mono-ui text-[10px] text-[#8D9A87]">
                // Must be a direct link to the audio file (not a YouTube / Spotify page).
              </p>
            </div>
          ) : (
            <div>
              <div className="label-tag mb-1">Audio file (max 12 MB) <span className="text-[#C0392B]">*</span></div>
              <label className="inline-flex items-center gap-2 px-4 py-2 border border-[#5C6B51] hover:border-[#C0A875] text-[#C0A875] font-mono-ui text-[11px] uppercase tracking-[0.25em] cursor-pointer">
                <Upload size={12} />
                {file ? file.name : "Pick MP3 / MP4 / M4A / WAV"}
                <input
                  data-testid="music-file-input"
                  type="file"
                  accept=".mp3,.mp4,.m4a,.wav,audio/*"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="hidden"
                />
              </label>
              {file && (
                <p className="mt-1 font-mono-ui text-[10px] text-[#8D9A87]">
                  // {Math.round(file.size / 1024)} KB · {file.type || "unknown type"}
                </p>
              )}
            </div>
          )}
        </div>
        <div className="mt-5 flex justify-end">
          <button
            data-testid="music-add-button"
            onClick={submit}
            disabled={busy}
            className="bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold inline-flex items-center gap-2"
          >
            {busy && <Loader2 className="animate-spin" size={12} />}
            Add track
          </button>
        </div>
      </div>

      <div>
        <h3 className="font-mono-ui text-xs uppercase tracking-[0.25em] text-[#8D9A87] mb-4">
          Playlist ({tracks.length})
        </h3>
        {loading && (
          <div className="text-center py-10">
            <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={20} />
          </div>
        )}
        {!loading && tracks.length === 0 && (
          <p className="text-[#8D9A87] text-sm font-mono-ui">// No tracks yet. Add one above.</p>
        )}
        <div className="space-y-2">
          {tracks.map((t, i) => (
            <div
              key={t.id}
              data-testid={`music-row-${t.id}`}
              className="flex items-center gap-4 p-3 bg-[#161D14] border border-[#283524]"
            >
              <div className="w-9 h-9 border border-[#283524] flex items-center justify-center bg-[#0D110C] shrink-0">
                <Music className="text-[#C0A875]" size={14} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display uppercase text-[#E2E4DF] text-sm truncate">{t.title}</div>
                <div className="font-mono-ui text-[10px] text-[#8D9A87] truncate uppercase tracking-[0.2em]">
                  {t.artist || "—"} · {t.source_type === "upload" ? "uploaded" : "URL"} · #{i + 1}
                </div>
              </div>
              <button
                data-testid={`music-delete-${t.id}`}
                onClick={() => remove(t)}
                className="border border-[#C0392B] text-[#C0392B] hover:bg-[#C0392B]/15 px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
              >
                <Trash2 size={11} /> Delete
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}


function FactionProposalsSection({ isSuperAdmin }) {
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [notesById, setNotesById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/admin/factions/proposals");
      setProposals(res.data.proposals || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load proposals.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const review = async (id, status) => {
    setBusyId(id);
    try {
      await api.patch(`/admin/factions/proposals/${id}`, {
        status,
        notes: (notesById[id] || "").trim() || null,
      });
      toast.success(status === "approved" ? "Faction approved & created." : "Proposal rejected.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Action failed.");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this proposal permanently?")) return;
    setBusyId(id);
    try {
      await api.delete(`/admin/factions/proposals/${id}`);
      toast.success("Proposal deleted.");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    } finally {
      setBusyId(null);
    }
  };

  if (!isSuperAdmin) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-5 text-[#8D9A87] text-sm">
        <span className="font-mono-ui">// Only the server owner can review faction proposals.</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="text-center py-16">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={24} />
      </div>
    );
  }

  if (!proposals.length) {
    return (
      <div
        data-testid="faction-proposals-empty"
        className="bg-[#161D14] border border-[#283524] p-10 text-center text-[#8D9A87]"
      >
        <Flag className="mx-auto text-[#C0A875]" size={32} />
        <p className="mt-4 font-mono-ui text-xs uppercase tracking-[0.25em]">
          // NO FACTION PROPOSALS YET
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="faction-proposals-list">
      {proposals.map((p) => {
        const isPending = p.status === "pending";
        const statusClass =
          p.status === "approved"
            ? "border-[#4ADE80] text-[#4ADE80]"
            : p.status === "rejected"
            ? "border-[#C0392B] text-[#C0392B]"
            : "border-[#C0A875] text-[#C0A875]";
        return (
          <div
            key={p.id}
            data-testid={`faction-proposal-${p.id}`}
            className="bg-[#161D14] border border-[#283524] p-5"
          >
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <FactionChip
                    faction={{ name: p.name, acronym: p.acronym, color: p.color }}
                    size="sm"
                    testid={`faction-proposal-chip-${p.id}`}
                  />
                  <h4 className="font-display uppercase text-[#E2E4DF] text-xl tracking-tight">
                    {p.name}
                  </h4>
                  <span
                    className={`inline-block border px-2 py-0.5 font-mono-ui text-[10px] uppercase tracking-[0.25em] ${statusClass}`}
                  >
                    {p.status}
                  </span>
                </div>
                <p className="mt-2 font-mono-ui text-[11px] text-[#8D9A87] uppercase tracking-[0.2em]">
                  by <span className="text-[#C0A875]">{p.character_name || p.discord_username}</span>{" "}
                  · @{p.discord_username} · ID {p.discord_id}
                </p>
                <p className="mt-3 text-[#E2E4DF] text-sm leading-relaxed max-w-3xl whitespace-pre-wrap">
                  {p.description}
                </p>
                {p.review_notes && (
                  <p className="mt-3 text-[#A8B5A0] text-sm italic">"{p.review_notes}"</p>
                )}
                <p className="mt-3 font-mono-ui text-[10px] text-[#5C6B51] uppercase tracking-[0.2em]">
                  submitted {new Date(p.submitted_at).toLocaleString()}
                  {p.reviewed_at ? ` · reviewed ${new Date(p.reviewed_at).toLocaleString()}` : ""}
                </p>
              </div>
            </div>

            {isPending && (
              <div className="mt-4 border-t border-[#283524] pt-4 space-y-3">
                <input
                  data-testid={`faction-proposal-notes-${p.id}`}
                  value={notesById[p.id] || ""}
                  onChange={(e) =>
                    setNotesById({ ...notesById, [p.id]: e.target.value })
                  }
                  placeholder="Optional review note (sent to proposer on rejection)…"
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
                />
                <div className="flex flex-wrap gap-2">
                  <button
                    data-testid={`faction-proposal-approve-${p.id}`}
                    disabled={busyId === p.id}
                    onClick={() => review(p.id, "approved")}
                    className="inline-flex items-center gap-2 bg-[#4ADE80]/15 hover:bg-[#4ADE80]/25 text-[#4ADE80] border border-[#4ADE80] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] disabled:opacity-50"
                  >
                    {busyId === p.id ? (
                      <Loader2 className="animate-spin" size={12} />
                    ) : (
                      <CheckCircle2 size={12} />
                    )}
                    Approve & create
                  </button>
                  <button
                    data-testid={`faction-proposal-reject-${p.id}`}
                    disabled={busyId === p.id}
                    onClick={() => review(p.id, "rejected")}
                    className="inline-flex items-center gap-2 bg-[#C0392B]/15 hover:bg-[#C0392B]/25 text-[#C0392B] border border-[#C0392B] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] disabled:opacity-50"
                  >
                    {busyId === p.id ? (
                      <Loader2 className="animate-spin" size={12} />
                    ) : (
                      <XCircle size={12} />
                    )}
                    Reject
                  </button>
                </div>
              </div>
            )}

            {!isPending && (
              <div className="mt-3 border-t border-[#283524] pt-3">
                <button
                  data-testid={`faction-proposal-delete-${p.id}`}
                  disabled={busyId === p.id}
                  onClick={() => remove(p.id)}
                  className="inline-flex items-center gap-2 text-[#8D9A87] hover:text-[#C0392B] font-mono-ui text-[11px] uppercase tracking-[0.25em] disabled:opacity-50"
                >
                  <Trash2 size={12} /> Delete record
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Small super-admin widget for editing the numbers that drive the Hero stats
 * (players online, max players, uptime days, and — most importantly for the
 * production drift issue — `whitelist_slots_taken`).
 */
function ServerStatsEditor() {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  // The only user-editable value in this widget. Derived from the loaded stats
  // (`total - taken`) but tracked separately so typing doesn't fight the API.
  const [pendingRemaining, setPendingRemaining] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/server/stats");
      setStats(res.data);
      const total = Number(res.data?.whitelist_slots_total) || 0;
      const taken = Number(res.data?.whitelist_slots_taken) || 0;
      setPendingRemaining(Math.max(0, total - taken));
      setDirty(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load application count.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      // We only expose "Remaining" to the admin. Preserve the current
      // slots_taken (accepted applications so far) and derive slots_total
      // from `taken + remaining` so the count stays consistent.
      const taken = Number(stats.whitelist_slots_taken) || 0;
      const remaining = Math.max(0, Number(pendingRemaining) || 0);
      const res = await api.patch("/server/stats", {
        whitelist_slots_total: taken + remaining,
      });
      toast.success("Application count updated.");
      setDirty(false);
      if (res.data?.updated) {
        setStats((s) => ({ ...(s || {}), ...res.data.updated }));
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !stats) {
    return (
      <div className="mb-6 bg-[#161D14] border border-[#283524] p-5 text-center">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
      </div>
    );
  }

  const slotsLeft = Math.max(0, Number(pendingRemaining) || 0);
  const set = (v) => {
    setPendingRemaining(v);
    setDirty(true);
  };

  return (
    <div
      data-testid="server-stats-editor"
      className="mb-6 bg-[#161D14] border border-[#283524] p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
            Application Count
          </h4>
          <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            // Approvals auto-decrement this. Set to 0 to close applications.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span
            data-testid="server-stats-slots-left"
            className={`font-mono-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 ${
              slotsLeft <= 5
                ? "text-[#C0392B] border-[#C0392B]/60"
                : "text-[#C0A875] border-[#C0A875]/40"
            }`}
          >
            {slotsLeft === 0 ? "closed" : `${slotsLeft} open`}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty}
            data-testid="server-stats-save"
            className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
          >
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="mt-4 max-w-xs">
        <StatInput
          label="Remaining Open Applications"
          value={pendingRemaining}
          onChange={set}
          testid="stats-slots-remaining"
        />
      </div>
    </div>
  );
}

function StatInput({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
        {label}
      </label>
      <input
        type="number"
        min="0"
        value={value ?? 0}
        onChange={(e) => onChange(e.target.value === "" ? 0 : Number(e.target.value))}
        data-testid={testid}
        className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
      />
    </div>
  );
}


/**
 * Home-page announcement bar editor (super-admin only).
 * Publishes to /api/content/announcement:
 *   { enabled, text, posted_at, event_at, info_enabled, info_text }
 * Red bar: text + optional live countdown to event_at + *New* badge (3 days).
 * Blue info bar: quieter secondary bar for small updates. Stacks under the red.
 * Changing the red text re-stamps posted_at server-side so the badge resets.
 */
function AnnouncementEditor() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(false);
  const [text, setText] = useState("");
  const [postedAt, setPostedAt] = useState(null);
  // datetime-local input value (empty string = no event)
  const [eventLocal, setEventLocal] = useState("");
  // Blue info bar state
  const [infoEnabled, setInfoEnabled] = useState(false);
  const [infoText, setInfoText] = useState("");
  const [initial, setInitial] = useState({
    enabled: false,
    text: "",
    eventLocal: "",
    infoEnabled: false,
    infoText: "",
  });
  const [tick, setTick] = useState(0);

  // Tick every second so the preview countdown ticks live
  useEffect(() => {
    const t = setInterval(() => setTick((n) => n + 1), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/content/announcement");
        if (cancelled) return;
        const d = res.data || {};
        const localFromIso = d.event_at ? isoToLocalInput(d.event_at) : "";
        setEnabled(!!d.enabled);
        setText(d.text || "");
        setPostedAt(d.posted_at || null);
        setEventLocal(localFromIso);
        setInfoEnabled(!!d.info_enabled);
        setInfoText(d.info_text || "");
        setInitial({
          enabled: !!d.enabled,
          text: d.text || "",
          eventLocal: localFromIso,
          infoEnabled: !!d.info_enabled,
          infoText: d.info_text || "",
        });
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load announcement.");
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const dirty =
    enabled !== initial.enabled ||
    text !== initial.text ||
    eventLocal !== initial.eventLocal ||
    infoEnabled !== initial.infoEnabled ||
    infoText !== initial.infoText;

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/announcement", {
        enabled,
        text,
        event_at: eventLocal ? localInputToIso(eventLocal) : "",
        info_enabled: infoEnabled,
        info_text: infoText,
      });
      toast.success("Announcement updated.");
      const d = res.data || {};
      const localFromIso = d.event_at ? isoToLocalInput(d.event_at) : "";
      setEnabled(!!d.enabled);
      setText(d.text || "");
      setPostedAt(d.posted_at || null);
      setEventLocal(localFromIso);
      setInfoEnabled(!!d.info_enabled);
      setInfoText(d.info_text || "");
      setInitial({
        enabled: !!d.enabled,
        text: d.text || "",
        eventLocal: localFromIso,
        infoEnabled: !!d.info_enabled,
        infoText: d.info_text || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const clear = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/announcement", {
        enabled: false,
        text: "",
        event_at: "",
        info_enabled: false,
        info_text: "",
      });
      toast.success("Announcement cleared.");
      const d = res.data || {};
      setEnabled(!!d.enabled);
      setText(d.text || "");
      setPostedAt(d.posted_at || null);
      setEventLocal("");
      setInfoEnabled(!!d.info_enabled);
      setInfoText(d.info_text || "");
      setInitial({
        enabled: !!d.enabled,
        text: d.text || "",
        eventLocal: "",
        infoEnabled: !!d.info_enabled,
        infoText: d.info_text || "",
      });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Clear failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="mb-6 bg-[#161D14] border border-[#283524] p-5 text-center">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
      </div>
    );
  }

  // Badge countdown status (admin summary chip)
  const postedMs = postedAt ? Date.parse(postedAt) : NaN;
  const NEW_MS = 3 * 24 * 60 * 60 * 1000;
  const elapsed = Number.isFinite(postedMs) ? Date.now() - postedMs : NaN;
  const badgeActive = Number.isFinite(elapsed) && elapsed < NEW_MS;
  const hoursLeft = Number.isFinite(elapsed)
    ? Math.max(0, Math.floor((NEW_MS - elapsed) / (60 * 60 * 1000)))
    : 0;

  // Preview countdown breakdown (depends on `tick` so it updates each second)
  const eventMs = eventLocal ? Date.parse(localInputToIso(eventLocal)) : NaN;
  const msLeft = Number.isFinite(eventMs) ? eventMs - Date.now() : NaN;
  const showPreviewCountdown = Number.isFinite(msLeft) && msLeft > 0;
  const cd = showPreviewCountdown ? announcementBreakdown(msLeft) : null;
  const eventPast =
    Number.isFinite(eventMs) && !showPreviewCountdown; // reference tick
  void tick;

  return (
    <div
      data-testid="announcement-editor"
      className="mb-6 bg-[#161D14] border border-[#283524] p-5"
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <Megaphone size={18} className="text-[#C0392B] mt-0.5" />
          <div>
            <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
              Home Announcement Bar
            </h4>
            <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
              // Red bar shown under Navbar on Home. Edit text → New badge resets for 3 days.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {enabled && badgeActive && (
            <span
              data-testid="announcement-badge-status"
              className="font-mono-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 text-[#F5D97A] border-[#F5D97A]/60"
            >
              New // {hoursLeft}h left
            </span>
          )}
          {enabled && !badgeActive && postedAt && (
            <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 text-[#8D9A87] border-[#283524]">
              Badge expired
            </span>
          )}
          <button
            type="button"
            onClick={clear}
            disabled={saving || (!enabled && !text && !eventLocal)}
            data-testid="announcement-clear"
            className="inline-flex items-center gap-2 border border-[#5C6B51] hover:border-[#C0392B] hover:text-[#C0392B] text-[#8D9A87] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <Trash2 size={12} /> Clear
          </button>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirty || (enabled && !text.trim())}
            data-testid="announcement-save"
            className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
          >
            {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 md:grid-cols-[auto_1fr] items-start">
        <label className="inline-flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            data-testid="announcement-enabled"
            className="accent-[#C0A875]"
          />
          <span className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#E2E4DF]">
            {enabled ? "Live on Home" : "Disabled"}
          </span>
        </label>

        <div className="space-y-4">
          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Announcement Text
            </label>
            <input
              type="text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              maxLength={140}
              placeholder="SEASON 1 DROP AUGUST 4TH 7PM EST"
              data-testid="announcement-text-input"
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
            />
            <p className="mt-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
              {text.length}/140 · Editing text and saving resets the {" "}
              <span className="text-[#F5D97A]">New</span> badge for 3 days.
            </p>
          </div>

          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Event Date &amp; Time (your local timezone) — optional
            </label>
            <div className="flex flex-wrap gap-2 items-center">
              <input
                type="datetime-local"
                value={eventLocal}
                onChange={(e) => setEventLocal(e.target.value)}
                data-testid="announcement-event-input"
                className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
              />
              {eventLocal && (
                <button
                  type="button"
                  onClick={() => setEventLocal("")}
                  data-testid="announcement-event-clear"
                  className="text-[#8D9A87] hover:text-[#C0392B] font-mono-ui text-[10px] uppercase tracking-[0.25em] px-2 py-2"
                >
                  ✕ clear date
                </button>
              )}
            </div>
            <p className="mt-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
              Live countdown ticks on the bar. Countdown hides after the event
              passes.
            </p>
          </div>
        </div>
      </div>

      {/* Live preview */}
      <div className="mt-5">
        <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-2">
          Preview
        </div>
        {enabled && text.trim() ? (
          <div className="bg-[#8B1A1A] border-b border-[#5a0f0f] h-10 px-4 flex items-center gap-3 overflow-hidden">
            <span aria-hidden className="text-[#F5D97A] text-sm">⚠</span>
            {badgeActive && (
              <span className="shrink-0 inline-flex items-center bg-[#F5D97A] text-[#5a0f0f] font-mono-ui text-[10px] uppercase tracking-[0.25em] font-bold px-2 py-0.5">
                New
              </span>
            )}
            <span className="font-mono-ui text-xs uppercase tracking-[0.18em] text-[#F5E9C8] font-semibold truncate">
              {text}
            </span>
            {showPreviewCountdown && cd && (
              <div
                data-testid="announcement-preview-countdown"
                className="ml-auto shrink-0 flex items-baseline gap-1.5 pl-3 border-l border-[#5a0f0f] font-mono-ui text-[#F5E9C8]"
              >
                {cd.days > 0 && (
                  <span className="tabular-nums text-xs font-bold">
                    {cd.days}
                    <span className="text-[10px] text-[#F5D97A]/80 ml-0.5">d</span>
                  </span>
                )}
                <span className="tabular-nums text-xs font-bold">
                  {String(cd.hours).padStart(2, "0")}
                  <span className="text-[10px] text-[#F5D97A]/80 ml-0.5">h</span>
                </span>
                <span className="tabular-nums text-xs font-bold">
                  {String(cd.mins).padStart(2, "0")}
                  <span className="text-[10px] text-[#F5D97A]/80 ml-0.5">m</span>
                </span>
                {cd.days === 0 && (
                  <span className="tabular-nums text-xs font-bold">
                    {String(cd.secs).padStart(2, "0")}
                    <span className="text-[10px] text-[#F5D97A]/80 ml-0.5">s</span>
                  </span>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="border border-dashed border-[#283524] px-4 h-10 flex items-center font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#5C6B51]">
            // Bar hidden
          </div>
        )}
        {eventPast && (
          <p className="mt-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0392B]">
            // Event date has passed — countdown auto-hidden on the live bar.
          </p>
        )}
      </div>

      {/* -------- Info bar (blue) — quieter updates -------- */}
      <div className="mt-8 pt-6 border-t border-[#283524]">
        <div className="flex items-start gap-3 mb-4">
          <div className="w-[18px] h-[18px] mt-0.5 flex items-center justify-center bg-[#1E3A5F] border border-[#12253d]">
            <span className="text-[#7DB0E8] text-xs font-bold">i</span>
          </div>
          <div>
            <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
              Info Bar (blue)
            </h4>
            <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
              // Quieter second bar for small updates. Stacks under the red bar.
            </p>
          </div>
        </div>

        <div className="grid gap-4 md:grid-cols-[auto_1fr] items-start">
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={infoEnabled}
              onChange={(e) => setInfoEnabled(e.target.checked)}
              data-testid="info-bar-enabled"
              className="accent-[#7DB0E8]"
            />
            <span className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#E2E4DF]">
              {infoEnabled ? "Live on Home" : "Disabled"}
            </span>
          </label>

          <div>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              Info Bar Text
            </label>
            <input
              type="text"
              value={infoText}
              onChange={(e) => setInfoText(e.target.value)}
              maxLength={140}
              placeholder="MAINTENANCE WINDOW SUN 3AM // 20 MIN DOWNTIME"
              data-testid="info-bar-text-input"
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#7DB0E8] focus:outline-none"
            />
            <p className="mt-1.5 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
              {infoText.length}/140 · No countdown, no <span className="text-[#F5D97A]">New</span> badge — just calm info.
            </p>
          </div>
        </div>

        <div className="mt-4">
          <div className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-2">
            Preview
          </div>
          {infoEnabled && infoText.trim() ? (
            <div className="bg-[#1E3A5F] border-b border-[#12253d] h-10 px-4 flex items-center gap-3 overflow-hidden">
              <span aria-hidden className="text-[#7DB0E8] text-sm font-bold">i</span>
              <span className="font-mono-ui text-xs uppercase tracking-[0.18em] text-[#DCE9F6] font-semibold truncate">
                {infoText}
              </span>
            </div>
          ) : (
            <div className="border border-dashed border-[#283524] px-4 h-10 flex items-center font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#5C6B51]">
              // Info bar hidden
            </div>
          )}
        </div>
      </div>

      <WelcomeBarSettings />
      <AnnouncementColors />
    </div>
  );
}

const COLOR_BARS = [
  { key: "red", label: "Red Announcement", sample: "SEASON 1 DROP AUGUST 4TH 7PM EST" },
  { key: "blue", label: "Blue Info Bar", sample: "MAINTENANCE WINDOW SUN 3AM" },
  { key: "welcome", label: "Gold Newest Survivor", sample: "WELCOME TO THE WALL," },
];
const COLOR_FIELDS = [
  { key: "bg", label: "Background" },
  { key: "text", label: "Text" },
  { key: "accent", label: "Icon / Accent" },
];
const HEX6 = /^#[0-9a-fA-F]{6}$/;

function AnnouncementColors() {
  const [colors, setColors] = useState(null);
  const [saved, setSaved] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/announcement").then((res) => {
      const c = res.data?.colors || DEFAULT_COLORS;
      setColors(c);
      setSaved(c);
    }).catch(() => { setColors(DEFAULT_COLORS); setSaved(DEFAULT_COLORS); });
  }, []);

  if (!colors) return null;
  const setField = (bar, k, v) => setColors((p) => ({ ...p, [bar]: { ...p[bar], [k]: v } }));
  const invalid = COLOR_BARS.some(({ key }) => COLOR_FIELDS.some((f) => !HEX6.test(colors[key][f.key] || "")));
  const dirty = JSON.stringify(colors) !== JSON.stringify(saved);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/announcement-colors", { colors });
      setColors(res.data.colors);
      setSaved(res.data.colors);
      toast.success("Bar colors saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-8 pt-6 border-t border-[#283524]" data-testid="announcement-colors">
      <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Bar Colors</h4>
      <p className="mt-1 mb-5 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
        // Customize each bar. Changes go live on Save.
      </p>
      <div className="space-y-6">
        {COLOR_BARS.map(({ key, label, sample }) => (
          <div key={key} data-testid={`colors-${key}`}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#E2E4DF]">{label}</span>
              <button
                type="button"
                onClick={() => setColors((p) => ({ ...p, [key]: { ...DEFAULT_COLORS[key] } }))}
                data-testid={`colors-${key}-reset`}
                className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87] hover:text-[#C0A875] transition-colors"
              >
                // reset to default
              </button>
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              {COLOR_FIELDS.map((f) => (
                <ColorField
                  key={f.key}
                  label={key === "welcome" && f.key === "accent" ? "Username" : f.label}
                  value={colors[key][f.key]}
                  onChange={(v) => setField(key, f.key, v)}
                  testid={`colors-${key}-${f.key}`}
                />
              ))}
            </div>
            <div
              className="mt-2 h-10 px-4 flex items-center gap-3 overflow-hidden font-mono-ui text-xs uppercase tracking-[0.18em] font-semibold"
              style={{ backgroundColor: colors[key].bg, color: colors[key].text }}
            >
              <span style={{ color: colors[key].accent }} aria-hidden>■</span>
              {sample}
              {key === "welcome" && (
                <span className="normal-case tracking-normal font-bold bg-[#0D110C] px-1.5 py-0.5" style={{ color: colors[key].accent }}>
                  @username
                </span>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="mt-5 flex items-center gap-3">
        <button
          type="button"
          onClick={save}
          disabled={!dirty || invalid || saving}
          data-testid="colors-save"
          className="bg-[#C0A875] hover:bg-[#a99362] disabled:opacity-40 text-[#0D110C] px-5 py-2 font-mono-ui text-xs uppercase tracking-[0.25em] font-semibold transition-colors"
        >
          {saving ? "Saving…" : "Save Colors"}
        </button>
        {invalid && <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#C0392B]">// Use #RRGGBB hex</span>}
      </div>
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  const valid = HEX6.test(value || "");
  return (
    <div>
      <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">{label}</label>
      <div className={`flex items-center bg-[#0D110C] border ${valid ? "border-[#283524]" : "border-[#C0392B]"} focus-within:border-[#C0A875]`}>
        <input
          type="color"
          value={valid ? value : "#000000"}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          data-testid={`${testid}-picker`}
          className="w-10 h-9 shrink-0 cursor-pointer bg-transparent border-0 p-1"
        />
        <span className="pl-1 text-[#5C6B51] font-mono-ui text-sm">#</span>
        <input
          type="text"
          value={(value || "").replace(/^#/, "")}
          onChange={(e) => onChange(`#${e.target.value.replace(/[^0-9a-fA-F]/g, "").slice(0, 6).toUpperCase()}`)}
          data-testid={`${testid}-hex`}
          className="w-full bg-transparent text-[#E2E4DF] px-1 py-2 font-mono-ui text-sm focus:outline-none"
          maxLength={6}
        />
      </div>
    </div>
  );
}

function WelcomeBarSettings() {
  const [state, setState] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/content/announcement")
      .then((res) => setState({ enabled: res.data?.welcome_enabled !== false, welcome: res.data?.welcome || null, colors: res.data?.colors }))
      .catch(() => setState({ enabled: true, welcome: null }));
  }, []);

  const toggle = async (enabled) => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/welcome-bar", { enabled });
      setState((p) => ({ ...p, enabled: !!res.data?.welcome_enabled, welcome: res.data?.welcome || null }));
      toast.success(enabled ? "Welcome bar enabled." : "Welcome bar disabled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (!state) return null;
  const w = state.welcome;
  return (
    <div className="mt-8 pt-6 border-t border-[#283524]" data-testid="welcome-bar-settings">
      <div className="flex items-start gap-3 mb-4">
        <div className="w-[18px] h-[18px] mt-0.5 bg-[#C0A875] border border-[#8f7c52]" />
        <div>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">Newest Survivor Bar (gold)</h4>
          <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            // Automatic — welcomes the latest approved applicant for 7 days or until the next approval.
          </p>
        </div>
      </div>
      <label className="inline-flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={state.enabled}
          disabled={saving}
          onChange={(e) => toggle(e.target.checked)}
          data-testid="welcome-bar-enabled"
          className="accent-[#C0A875]"
        />
        <span className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#E2E4DF]">
          {state.enabled ? "Live on Home" : "Disabled"} · saves instantly
        </span>
      </label>
      <div className="mt-4 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-2">Currently showing</div>
      {state.enabled && w ? (
        <WelcomeBar welcome={w} c={state.colors?.welcome || DEFAULT_COLORS.welcome} preview />
      ) : (
        <div className="border border-dashed border-[#283524] px-4 h-10 flex items-center font-mono-ui text-[11px] uppercase tracking-[0.2em] text-[#5C6B51]">
          {state.enabled ? "// No approvals in the last 7 days" : "// Welcome bar hidden"}
        </div>
      )}
    </div>
  );
}

// datetime-local helpers.
// `datetime-local` input value looks like "2026-08-04T19:00" in *local* time.
function isoToLocalInput(iso) {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(
    d.getHours()
  )}:${p(d.getMinutes())}`;
}

function localInputToIso(local) {
  // Browser parses "YYYY-MM-DDTHH:mm" as local time; toISOString gives UTC.
  const d = new Date(local);
  if (isNaN(d.getTime())) return "";
  return d.toISOString();
}

function announcementBreakdown(ms) {
  const totalSecs = Math.floor(ms / 1000);
  const days = Math.floor(totalSecs / 86400);
  const hours = Math.floor((totalSecs % 86400) / 3600);
  const mins = Math.floor((totalSecs % 3600) / 60);
  const secs = totalSecs % 60;
  return { days, hours, mins, secs };
}

/**
 * Super-admin diagnostic panel. Shows the most recent whitelist submissions
 * that got rejected by the backend and WHY — so when a survivor reports "I
 * filled it out but you don't see it pending", you can immediately see if
 * they hit VaultCord, slots-closed, duplicate, or bailed on a validation
 * error. Data source: GET /api/whitelist/submit-failures (super-admin only).
 */
const FAILURE_REASON_LABEL = {
  not_in_discord_guild: "Not in Discord server",
  not_vaultcord_verified: "Not VaultCord-verified",
  slots_closed: "Applications closed (0 slots)",
  rules_not_accepted: "Rules not accepted",
  backstory_too_short: "Backstory < 100 chars",
  scenario_too_short: "Scenario answer < 100 chars",
  bad_server_username: "Invalid server username",
  missing_server_password: "Server password missing",
  duplicate_pending: "Already had a pending app",
  not_logged_in: "Discord login expired / not logged in",
};

function SubmitFailuresPanel() {
  const [failures, setFailures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/whitelist/submit-failures");
      setFailures(res.data?.failures || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load submit failures.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = failures.length;

  return (
    <div
      data-testid="submit-failures-panel"
      className="mb-6 bg-[#161D14] border border-[#283524]"
    >
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid="submit-failures-toggle"
        className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-[#1a221a] transition-colors"
      >
        <div className="flex items-start gap-3">
          <AlertOctagon size={18} className="text-[#C0A875] mt-0.5 shrink-0" />
          <div>
            <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
              Submission Failures
            </h4>
            <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
              // Rejected whitelist attempts (last 50). Trace &quot;I filled it out&quot; reports.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span
            data-testid="submit-failures-count"
            className={`font-mono-ui text-[10px] uppercase tracking-[0.25em] border px-3 py-1.5 ${
              total > 0
                ? "text-[#C0392B] border-[#C0392B]/60"
                : "text-[#8D9A87] border-[#283524]"
            }`}
          >
            {total} logged
          </span>
          <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            {open ? "hide" : "show"}
          </span>
        </div>
      </button>

      {open && (
        <div className="border-t border-[#283524] p-5">
          <div className="flex justify-end mb-3">
            <button
              type="button"
              onClick={load}
              disabled={loading}
              data-testid="submit-failures-refresh"
              className="inline-flex items-center gap-2 text-[#8D9A87] hover:text-[#C0A875] font-mono-ui text-[10px] uppercase tracking-[0.25em] px-2 py-1"
            >
              <RefreshCw size={12} className={loading ? "animate-spin" : ""} />
              Refresh
            </button>
          </div>
          {loading && (
            <div className="text-center py-4">
              <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
            </div>
          )}
          {!loading && total === 0 && (
            <p className="text-[#8D9A87] text-sm font-mono-ui text-center py-4">
              // No rejected submissions yet.
            </p>
          )}
          {!loading && total > 0 && (
            <div className="space-y-2">
              {failures.map((f) => (
                <div
                  key={f.id}
                  data-testid={`submit-failure-${f.id}`}
                  className="grid grid-cols-1 md:grid-cols-[auto_1fr_auto] gap-2 md:gap-4 items-center bg-[#0D110C] border border-[#283524] px-3 py-2"
                >
                  <span className="font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#8D9A87] whitespace-nowrap">
                    {new Date(f.at).toLocaleString()}
                  </span>
                  <span className="font-mono-ui text-xs text-[#E2E4DF] truncate">
                    <span className="text-[#C0A875]">
                      @{f.discord_username || "unknown"}
                    </span>
                    {f.discord_global_name && (
                      <span className="text-[#8D9A87]"> · {f.discord_global_name}</span>
                    )}
                  </span>
                  <span
                    className={`font-mono-ui text-[10px] uppercase tracking-[0.2em] border px-2 py-1 whitespace-nowrap ${
                      f.status >= 500
                        ? "text-[#C0392B] border-[#C0392B]/60"
                        : "text-[#F5D97A] border-[#F5D97A]/40"
                    }`}
                  >
                    {FAILURE_REASON_LABEL[f.reason] || f.reason} · {f.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}


// -----------------------------------------------------------------------
// Home page editor — flat text fields, one per Hero slot. Fetches from
// GET /api/content/home and writes with PUT. Backend fills any missing
// fields with defaults so a blank input still round-trips safely.
// -----------------------------------------------------------------------
const HOME_FIELDS = [
  { key: "stat_coords", label: "Stat strip · Coordinates", hint: "Top-left of hero, next to the radio icon." },
  { key: "stat_region", label: "Stat strip · Region", hint: "Middle. Prefix with a dash for the look." },
  { key: "stat_era", label: "Stat strip · Era", hint: "Right side of stat strip." },
  { key: "title_line1", label: "Title · Line 1", hint: "First word/phrase of the huge headline." },
  { key: "title_line2_prefix", label: "Title · Line 2 accent", hint: "The gold flickering word on the second line." },
  { key: "title_line2_suffix", label: "Title · Line 2 rest", hint: "The rest of line 2 after the accent." },
  { key: "description", label: "Description paragraph", multiline: true },
  { key: "cta_primary", label: "Primary CTA label", hint: "Gold button. Include arrow if you want one." },
  { key: "cta_secondary", label: "Secondary CTA label", hint: "Outlined button — jumps to /lore." },
  { key: "scroll_hint", label: "Scroll hint", hint: "Small label under the bouncing arrow." },
];

function HomeEditor() {
  const [values, setValues] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/content/home");
        if (cancelled) return;
        const c = res.data?.content || {};
        setValues(c);
        setInitial(c);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load home content.");
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = values && initial && HOME_FIELDS.some((f) => (values[f.key] || "") !== (initial[f.key] || ""));

  const save = async () => {
    setSaving(true);
    try {
      const payload = Object.fromEntries(HOME_FIELDS.map((f) => [f.key, values[f.key] || ""]));
      const res = await api.put("/admin/content/home", { content: payload });
      toast.success("Home page saved.");
      setValues(res.data.content);
      setInitial(res.data.content);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !values) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-6 text-center">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
      </div>
    );
  }

  return (
    <div className="bg-[#161D14] border border-[#283524] p-5 space-y-4" data-testid="home-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
            Home Page Text
          </h4>
          <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            // Every text slot on the Home hero. Blank = restore default.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="home-save"
          className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
        >
          {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
      <div className="grid gap-4">
        {HOME_FIELDS.map((f) => (
          <div key={f.key}>
            <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
              {f.label}
            </label>
            {f.multiline ? (
              <textarea
                value={values[f.key] || ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                rows={4}
                data-testid={`home-field-${f.key}`}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
              />
            ) : (
              <input
                type="text"
                value={values[f.key] || ""}
                onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                data-testid={`home-field-${f.key}`}
                className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
              />
            )}
            {f.hint && (
              <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
                // {f.hint}
              </p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

// -----------------------------------------------------------------------
// Lore page editor — only edits the section header (eyebrow / title /
// description). Narrative paragraphs + timeline are still hardcoded in
// Lore.jsx and will get their own structured editor in a later pass.
// -----------------------------------------------------------------------
function FeaturesEditor() {
  // The "Principles" grid on the Home page — five feature cards with
  // editable icon, title, body, span, icon color and background image.
  // Cards are stored as an ordered array so we always render / save
  // exactly five entries.
  const [features, setFeatures] = useState(null);
  const [initial, setInitial] = useState(null);
  const [allowedIcons, setAllowedIcons] = useState([]);
  const [allowedSpans, setAllowedSpans] = useState([]);
  const [allowedPositions, setAllowedPositions] = useState([]);
  const [allowedSizes, setAllowedSizes] = useState([]);
  const [header, setHeader] = useState(null);
  const [initialHeader, setInitialHeader] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/content/features");
        if (cancelled) return;
        setFeatures(res.data.features);
        setInitial(res.data.features);
        setHeader(res.data.header);
        setInitialHeader(res.data.header);
        setAllowedIcons(res.data.allowed_icons || []);
        setAllowedSpans(res.data.allowed_spans || []);
        setAllowedPositions(res.data.allowed_positions || []);
        setAllowedSizes(res.data.allowed_sizes || []);
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load feature cards.");
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = features && initial && (
    JSON.stringify(features) !== JSON.stringify(initial) ||
    JSON.stringify(header) !== JSON.stringify(initialHeader)
  );

  const patchCard = (idx, patch) => {
    setFeatures((prev) => prev.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  };

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/features", { features, header });
      setFeatures(res.data.features);
      setInitial(res.data.features);
      setHeader(res.data.header);
      setInitialHeader(res.data.header);
      toast.success("Feature cards saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !features) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-6 text-center">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
      </div>
    );
  }

  return (
    <div className="bg-[#161D14] border border-[#283524] p-5 space-y-4" data-testid="features-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
            Home "Principles" Cards
          </h4>
          <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            // Icon, title, body, background & icon color for each of the 5 cards on the Home page
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="features-save"
          className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
        >
          {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
          {saving ? "Saving…" : "Save all"}
        </button>
      </div>

      {header && <FeaturesHeaderEditor header={header} onChange={setHeader} />}

      <div className="grid gap-4">
        {features.map((card, idx) => (
          <div
            key={idx}
            data-testid={`features-card-${idx}`}
            className="border border-[#283524] bg-[#0D110C]/60 p-4"
          >
            <div className="flex items-center justify-between mb-3">
              <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#5C6B51]">
                // Card 0{idx + 1}
              </span>
              {/* Live preview swatch — icon on top of image URL, respecting
                  the currently-selected position + size. */}
              <div
                className="relative w-32 h-16 border border-[#283524] overflow-hidden shrink-0"
                title="Live preview"
                data-testid={`features-preview-${idx}`}
              >
                {card.image && (
                  <div
                    className="absolute inset-0 opacity-70 bg-no-repeat"
                    style={{
                      backgroundImage: `url(${card.image})`,
                      backgroundSize: card.image_size || "cover",
                      backgroundPosition: card.image_position || "center",
                    }}
                  />
                )}
                <div className="absolute inset-0 flex items-center justify-center">
                  <span
                    className="font-display text-xs drop-shadow-[0_1px_2px_rgba(0,0,0,0.9)]"
                    style={{ color: card.icon_color || "#C0A875" }}
                  >
                    {card.icon}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid gap-3 md:grid-cols-2">
              <div>
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Title
                </label>
                <input
                  type="text"
                  value={card.title || ""}
                  onChange={(e) => patchCard(idx, { title: e.target.value })}
                  maxLength={120}
                  data-testid={`features-title-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
                />
              </div>
              <div>
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Icon
                </label>
                <select
                  value={card.icon || "Shield"}
                  onChange={(e) => patchCard(idx, { icon: e.target.value })}
                  data-testid={`features-icon-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
                >
                  {allowedIcons.map((name) => (
                    <option key={name} value={name}>{name}</option>
                  ))}
                </select>
              </div>

              <div className="md:col-span-2">
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Body
                </label>
                <textarea
                  value={card.body || ""}
                  onChange={(e) => patchCard(idx, { body: e.target.value })}
                  rows={2}
                  maxLength={400}
                  data-testid={`features-body-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
                />
              </div>

              <div className="md:col-span-2">
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Background image URL
                </label>
                <input
                  type="text"
                  value={card.image || ""}
                  onChange={(e) => patchCard(idx, { image: e.target.value })}
                  placeholder="https://…"
                  data-testid={`features-image-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs focus:border-[#C0A875] focus:outline-none"
                />
              </div>

              <div>
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Icon color (hex)
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={card.icon_color || "#C0A875"}
                    onChange={(e) => patchCard(idx, { icon_color: e.target.value.toUpperCase() })}
                    data-testid={`features-icon-color-native-${idx}`}
                    className="w-10 h-9 bg-[#0D110C] border border-[#283524] cursor-pointer"
                  />
                  <input
                    type="text"
                    value={card.icon_color || "#C0A875"}
                    onChange={(e) => {
                      let v = e.target.value.trim().toUpperCase();
                      if (v && !v.startsWith("#")) v = "#" + v;
                      patchCard(idx, { icon_color: v.slice(0, 7) });
                    }}
                    maxLength={7}
                    data-testid={`features-icon-color-hex-${idx}`}
                    className="flex-1 bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-xs uppercase focus:border-[#C0A875] focus:outline-none"
                  />
                </div>
              </div>

              <div>
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Grid width
                </label>
                <select
                  value={card.span || "md:col-span-6"}
                  onChange={(e) => patchCard(idx, { span: e.target.value })}
                  data-testid={`features-span-${idx}`}
                  className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
                >
                  {allowedSpans.map((s) => (
                    <option key={s} value={s}>{s.replace("md:col-span-", "")} / 12 cols</option>
                  ))}
                </select>
              </div>

              {/* Image position — 3×3 keyword grid + fine-tune sliders.
                  Click a cell to anchor to that preset, or drag the X/Y
                  sliders for pixel-perfect placement. Payload is still
                  allow-listed server-side so neither path can inject CSS. */}
              <div className="md:col-span-2">
                <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
                  Image position
                </label>
                <div className="flex items-start gap-3 flex-wrap">
                  <div
                    className="inline-grid grid-cols-3 gap-0.5 border border-[#283524] bg-[#0D110C] p-1"
                    data-testid={`features-position-grid-${idx}`}
                  >
                    {allowedPositions.map((pos) => {
                      // Highlight the preset cell only when the stored value
                      // matches it exactly (so the dot goes dark once the
                      // admin starts fine-tuning with the sliders).
                      const active = (card.image_position || "center") === pos;
                      return (
                        <button
                          key={pos}
                          type="button"
                          onClick={() => patchCard(idx, { image_position: pos })}
                          title={pos}
                          data-testid={`features-position-${idx}-${pos.replace(" ", "-")}`}
                          className={`w-6 h-6 border transition-colors ${
                            active
                              ? "border-[#C0A875] bg-[#C0A875]/20"
                              : "border-[#283524] hover:border-[#5C6B51]"
                          }`}
                        >
                          {active && (
                            <span className="block w-1.5 h-1.5 bg-[#C0A875] rounded-full mx-auto" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                  <div className="flex flex-col gap-1">
                    <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
                      Current: {card.image_position || "center"}
                    </span>
                    <div className="inline-flex border border-[#283524]">
                      {allowedSizes.map((s) => {
                        const active = (card.image_size || "cover") === s;
                        return (
                          <button
                            key={s}
                            type="button"
                            onClick={() => patchCard(idx, { image_size: s })}
                            data-testid={`features-size-${idx}-${s}`}
                            className={`px-3 py-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] transition-colors ${
                              active
                                ? "bg-[#C0A875] text-[#0D110C]"
                                : "text-[#8D9A87] hover:text-[#E2E4DF]"
                            }`}
                            title={
                              s === "cover"
                                ? "Fill the card; crop overflow"
                                : "Fit whole image inside; may show empty edges"
                            }
                          >
                            {s}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                  <DragPositionBox
                    image={card.image}
                    size={card.image_size || "cover"}
                    value={card.image_position || "center"}
                    onChange={(v) => patchCard(idx, { image_position: v })}
                    testIdx={idx}
                  />
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// -----------------------------------------------------------------------
// Fine Position Sliders — numeric X/Y % override for a Features card.
// Lets the admin dial in the background-position without being stuck on
// the 3×3 keyword presets. Writes back as "NN% NN%" which the backend
// validates against a strict regex before saving.
// -----------------------------------------------------------------------
// Preset keywords → their equivalent CSS "X% Y%" position. Lets us flip the
// sliders to the correct dot the moment the admin clicks a cell in the grid.
const PRESET_POSITION_TO_XY = {
  "top left": [0, 0], "top": [50, 0], "top right": [100, 0],
  "left": [0, 50], "center": [50, 50], "right": [100, 50],
  "bottom left": [0, 100], "bottom": [50, 100], "bottom right": [100, 100],
};

function parsePosition(value) {
  const v = (value || "center").trim().toLowerCase();
  if (PRESET_POSITION_TO_XY[v]) return PRESET_POSITION_TO_XY[v];
  const m = v.match(/^(\d{1,3})%\s+(\d{1,3})%$/);
  if (m) {
    const x = Math.min(100, Math.max(0, parseInt(m[1], 10)));
    const y = Math.min(100, Math.max(0, parseInt(m[2], 10)));
    return [x, y];
  }
  return [50, 50];
}

function DragPositionBox({ image, size, value, onChange, testIdx }) {
  const [x, y] = parsePosition(value);
  const { dragging, handlers } = useDragFocus({
    x, y, enabled: !!image,
    onChange: (nx, ny) => onChange(`${nx}% ${ny}%`),
  });

  return (
    <div className="flex flex-col gap-1.5" data-testid={`features-drag-position-${testIdx}`}>
      <span className="font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51]">
        {image ? "Drag to frame" : "Add an image URL to frame it"} · {x}% / {y}%
      </span>
      <div
        {...handlers}
        data-testid={`features-drag-box-${testIdx}`}
        className={`relative w-72 aspect-[21/9] border bg-[#0D110C] bg-no-repeat select-none overflow-hidden transition-colors ${
          dragging ? "border-[#C0A875]" : "border-[#283524] hover:border-[#5C6B51]"
        }`}
        style={{
          ...handlers.style,
          backgroundImage: image ? `url(${image})` : undefined,
          backgroundSize: size,
          backgroundPosition: `${x}% ${y}%`,
        }}
      >
        <span
          className="pointer-events-none absolute w-3 h-3 -ml-1.5 -mt-1.5 rounded-full border-2 border-[#C0A875] bg-[#0D110C]/40 shadow-[0_0_6px_rgba(0,0,0,0.8)]"
          style={{ left: `${x}%`, top: `${y}%` }}
        />
      </div>
      <button
        type="button"
        onClick={() => onChange("center")}
        data-testid={`features-fine-reset-${testIdx}`}
        className="self-end font-mono-ui text-[9px] uppercase tracking-[0.25em] text-[#5C6B51] hover:text-[#C0A875]"
      >
        // reset to center
      </button>
    </div>
  );
}

// -----------------------------------------------------------------------
// Lore page editor — only edits the section header (eyebrow / title /
// description). Narrative paragraphs + timeline are still hardcoded in
// Lore.jsx and will get their own structured editor in a later pass.
// -----------------------------------------------------------------------
function LoreEditor() {
  const [content, setContent] = useState(null);
  const [initial, setInitial] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/content/lore");
        if (cancelled) return;
        const c = res.data?.content || {};
        const next = {
          eyebrow: c.eyebrow || "",
          title: c.title || "",
          desc: c.desc || "",
          narrative: Array.isArray(c.narrative) ? c.narrative : [],
        };
        setContent(next);
        setInitial(JSON.parse(JSON.stringify(next)));
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Failed to load lore content.");
      } finally {
        !cancelled && setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const dirty = content && initial && JSON.stringify(content) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const res = await api.put("/admin/content/lore", { content: { ...content, blocks: [] } });
      toast.success("Lore page saved.");
      const c = res.data.content;
      const next = {
        eyebrow: c.eyebrow || "",
        title: c.title || "",
        desc: c.desc || "",
        narrative: Array.isArray(c.narrative) ? c.narrative : [],
      };
      setContent(next);
      setInitial(JSON.parse(JSON.stringify(next)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !content) {
    return (
      <div className="bg-[#161D14] border border-[#283524] p-6 text-center">
        <Loader2 className="animate-spin text-[#C0A875] mx-auto" size={18} />
      </div>
    );
  }

  const updateNarr = (i, patch) =>
    setContent({ ...content, narrative: content.narrative.map((p, pi) => (pi === i ? { ...p, ...patch } : p)) });
  const removeNarr = (i) =>
    setContent({ ...content, narrative: content.narrative.filter((_, pi) => pi !== i) });
  const moveNarr = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= content.narrative.length) return;
    const next = content.narrative.slice();
    [next[i], next[j]] = [next[j], next[i]];
    setContent({ ...content, narrative: next });
  };
  const addNarr = () =>
    setContent({ ...content, narrative: [...content.narrative, { style: "default", text: "New paragraph…" }] });

  return (
    <div className="bg-[#161D14] border border-[#283524] p-5 space-y-5" data-testid="lore-editor">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h4 className="font-display uppercase text-[#E2E4DF] text-lg tracking-tight">
            Lore Page
          </h4>
          <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
            // Header + narrative paragraphs. Timeline still hardcoded.
          </p>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving || !dirty}
          data-testid="lore-save"
          className="inline-flex items-center gap-2 bg-[#C0A875] hover:bg-[#a99362] disabled:bg-[#283524] disabled:text-[#8D9A87] text-[#0D110C] px-4 py-2 font-mono-ui text-[11px] uppercase tracking-[0.25em] font-semibold"
        >
          {saving ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />}
          {saving ? "Saving…" : "Save"}
        </button>
      </div>

      <div className="grid gap-3">
        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
            Eyebrow
          </label>
          <input
            type="text"
            value={content.eyebrow}
            onChange={(e) => setContent({ ...content, eyebrow: e.target.value })}
            data-testid="lore-eyebrow"
            className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
          />
        </div>
        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
            Title
          </label>
          <input
            type="text"
            value={content.title}
            onChange={(e) => setContent({ ...content, title: e.target.value })}
            data-testid="lore-title"
            className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
          />
        </div>
        <div>
          <label className="block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5">
            Description
          </label>
          <textarea
            value={content.desc}
            onChange={(e) => setContent({ ...content, desc: e.target.value })}
            rows={3}
            data-testid="lore-desc"
            className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
          />
        </div>
      </div>

      {/* Narrative paragraphs — left column of the Lore section */}
      <div className="border-t border-[#283524] pt-4 space-y-3">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div>
            <div className="font-mono-ui text-[11px] uppercase tracking-[0.25em] text-[#C0A875]">
              Narrative Paragraphs
            </div>
            <p className="mt-1 font-mono-ui text-[10px] uppercase tracking-[0.2em] text-[#5C6B51]">
              // Style options: Normal · Drop Cap (large gold first letter) · Accent (gold headline)
            </p>
          </div>
          <button
            type="button"
            onClick={addNarr}
            data-testid="lore-narr-add"
            className="inline-flex items-center gap-1 border border-[#5C6B51] hover:border-[#C0A875] hover:text-[#C0A875] text-[#8D9A87] px-3 py-1.5 font-mono-ui text-[10px] uppercase tracking-[0.25em]"
          >
            <Plus size={11} /> Paragraph
          </button>
        </div>

        {content.narrative.length === 0 && (
          <p className="text-center text-[#5C6B51] font-mono-ui text-[10px] uppercase tracking-[0.25em] py-4">
            // No paragraphs — click Paragraph above to add one.
          </p>
        )}

        {content.narrative.map((p, i) => (
          <div
            key={i}
            className="bg-[#0D110C] border border-[#283524] p-3 space-y-2"
            data-testid={`lore-narr-${i}`}
          >
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87]">
                  #{i + 1}
                </span>
                <select
                  value={p.style || "default"}
                  onChange={(e) => updateNarr(i, { style: e.target.value })}
                  data-testid={`lore-narr-style-${i}`}
                  className="bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-2 py-1 font-mono-ui text-[10px] uppercase focus:border-[#C0A875] focus:outline-none"
                >
                  <option value="default">Normal</option>
                  <option value="drop_cap">Drop Cap</option>
                  <option value="accent">Accent</option>
                </select>
              </div>
              <div className="flex gap-1">
                <button type="button" onClick={() => moveNarr(i, -1)} disabled={i === 0}
                  data-testid={`lore-narr-up-${i}`}
                  className="w-6 h-6 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] font-mono-ui text-xs disabled:opacity-30">↑</button>
                <button type="button" onClick={() => moveNarr(i, 1)} disabled={i === content.narrative.length - 1}
                  data-testid={`lore-narr-down-${i}`}
                  className="w-6 h-6 border border-[#283524] text-[#8D9A87] hover:border-[#C0A875] hover:text-[#C0A875] font-mono-ui text-xs disabled:opacity-30">↓</button>
                <button type="button" onClick={() => removeNarr(i)}
                  data-testid={`lore-narr-delete-${i}`}
                  className="w-6 h-6 border border-[#283524] text-[#C0392B] hover:border-[#C0392B] font-mono-ui text-xs flex items-center justify-center"><Trash2 size={11} /></button>
              </div>
            </div>
            <textarea
              value={p.text || ""}
              onChange={(e) => updateNarr(i, { text: e.target.value })}
              rows={p.style === "accent" ? 2 : 5}
              data-testid={`lore-narr-text-${i}`}
              className="w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none"
            />
          </div>
        ))}
      </div>
    </div>
  );
}


function FeaturesHeaderEditor({ header, onChange }) {
  const set = (k) => (e) => onChange({ ...header, [k]: e.target.value });
  const input = "w-full bg-[#0D110C] border border-[#283524] text-[#E2E4DF] px-3 py-2 font-mono-ui text-sm focus:border-[#C0A875] focus:outline-none";
  const label = "block font-mono-ui text-[10px] uppercase tracking-[0.25em] text-[#8D9A87] mb-1.5";
  return (
    <div className="border border-[#C0A875]/40 bg-[#0D110C]/60 p-4" data-testid="features-header-editor">
      <span className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-[#C0A875]">// Section header</span>
      <div className="mt-3 grid gap-3 md:grid-cols-[220px_1fr]">
        <div>
          <label className={label}>Eyebrow</label>
          <input type="text" value={header.eyebrow} onChange={set("eyebrow")} maxLength={60} data-testid="features-header-eyebrow" className={input} />
        </div>
        <div>
          <label className={label}>Title</label>
          <input type="text" value={header.title} onChange={set("title")} maxLength={120} data-testid="features-header-title" className={input} />
        </div>
      </div>
      <div className="mt-3">
        <label className={label}>Description</label>
        <textarea value={header.desc} onChange={set("desc")} maxLength={500} rows={2} data-testid="features-header-desc" className={`${input} resize-y`} />
      </div>
      <p className="mt-2 font-mono-ui text-[10px] text-[#5C6B51]">Leave a field empty to restore its default.</p>
    </div>
  );
}

function DmStatusChip({ app }) {
  const dm = app.dm_status || {};
  const events = ["submitted", "approved", "rejected"].filter((e) => dm[e]);
  if (!events.length) return null;
  const bad = events.filter((e) => dm[e] !== "sent");
  const title = events.map((e) => `${e}: ${dm[e]}`).join(" · ");
  if (!bad.length) {
    return (
      <span title={`Discord DMs: ${title}`} data-testid={`app-dm-ok-${app.id}`} className="font-mono-ui text-[9px] uppercase tracking-[0.2em] text-[#5C6B51]">
        DM ✓
      </span>
    );
  }
  const blocked = bad.some((e) => dm[e] === "blocked");
  return (
    <span
      title={`Discord DMs: ${title}. ${blocked ? "They have DMs from server members turned off, so reach out another way." : "Discord rejected the DM."}`}
      data-testid={`app-dm-blocked-${app.id}`}
      className="px-2 py-1 border border-[#C0392B]/50 bg-[#C0392B]/10 text-[#E57368] font-mono-ui text-[9px] uppercase tracking-[0.2em]"
    >
      {blocked ? "DM blocked" : "DM failed"}
    </span>
  );
}
